import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import EnhancedXComControls from '../components/EnhancedXComControls';
import TweetSubmissionForm from '../components/TweetSubmissionForm';
import TweetQueueManager from '../components/TweetQueueManager';
import PredictionReview from '../components/PredictionReview';
import QuoteReview from '../components/QuoteReview';
import LinkSubmissionsAdmin from '../components/LinkSubmissionsAdmin';
import PredictionManager from '../components/PredictionManager';


const apiBase = process.env.NODE_ENV === 'production' 
  ? 'https://proofofprice.com' 
  : 'http://localhost:3001';



// Styled components for X.com controls
const XComSection = styled.div`
  background: ${props => props.theme.surface};
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
  color: ${props => props.theme.text};
  margin-bottom: 20px;
  font-size: 20px;
`;

const ConnectionStatus = styled.div<{ status: string }>`
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 20px;
  background: ${props => {
    if (props.status === 'connected') return '#4CAF5020';
    if (props.status === 'error') return '#f4433620';
    return '#ff980020';
  }};
  border: 1px solid ${props => {
    if (props.status === 'connected') return '#4CAF50';
    if (props.status === 'error') return '#f44336';
    return '#ff9800';
  }};
  color: ${props => props.theme.text};
`;

const ControlSection = styled.div`
  margin-bottom: 30px;
  
  h3 {
    color: ${props => props.theme.text};
    margin-bottom: 15px;
  }
`;

const TweetTextArea = styled.textarea`
  width: 100%;
  height: 100px;
  padding: 10px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  background: ${props => props.theme.background};
  color: ${props => props.theme.text};
  resize: vertical;
  font-family: inherit;
  font-size: 14px;
`;

const CharCount = styled.div`
  text-align: right;
  color: ${props => props.theme.subText};
  font-size: 12px;
  margin-top: 5px;
  margin-bottom: 10px;
`;

const PostButton = styled.button`
  background: #1DA1F2;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 20px;
  cursor: pointer;
  font-weight: bold;
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    background: #0d8bd9;
  }
`;

const QuickActions = styled.div`
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  background: ${props => props.theme.primary};
  color: white;
  border: none;
  padding: 10px 15px;
  border-radius: 4px;
  cursor: pointer;
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
  
  @media (max-width: 768px) {
    padding: 12px 16px;
    font-size: 14px;
    min-height: 44px;
    touch-action: manipulation;
  }
`;

const EditToggleButton = styled.button`
  background: ${props => props.theme.surface};
  color: ${props => props.theme.primary};
  border: 2px solid ${props => props.theme.primary};
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  margin-bottom: 15px;
  
  &:hover {
    background: ${props => props.theme.primary};
    color: white;
  }
  
  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 12px;
    margin-bottom: 10px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 20px;
  
  @media (max-width: 768px) {
    gap: 8px;
    margin-top: 15px;
  }
`;

const ResultSection = styled.div`
  margin-top: 20px;
  
  h3 {
    color: ${props => props.theme.text};
    margin-bottom: 10px;
  }
`;

const ResultBox = styled.div<{ success: boolean }>`
  padding: 15px;
  border-radius: 4px;
  background: ${props => props.success ? '#4CAF5020' : '#f4433620'};
  border: 1px solid ${props => props.success ? '#4CAF50' : '#f44336'};
  color: ${props => props.theme.text};
  
  a {
    color: ${props => props.theme.primary};
    margin-left: 10px;
  }
`;

// Type definitions
interface Submission {
  id: number;
  expert_name: string;
  target_time: string;
  status: 'pending' | 'approved' | 'rejected' | 'processed';
  prediction_outcome?: 'pending' | 'success' | 'miss' | 'partial' | 'ongoing';
  created_at?: string;
  reviewed?: boolean;
  admin_comment?: string;
  
  // Add missing properties causing TypeScript errors
  submission_date?: string; // Add this property to fix the above error
  prediction_made?: string; // NEW: When the prediction was actually made
  influencer_id?: number | null; // Allow null values
  existing_influencer_id?: number | null; // Allow null values
  existing_influencer_name?: string;
  price_prediction?: string;
  submitter_email?: string;
  
  // Add other properties from the backend
  price_target?: number;
  price_min?: number;
  price_max?: number;
  prediction_type?: string;
  source_url?: string;
  twitter_handle?: string;
  notes?: string;
  quote?: string; // Quote field for editing in modal
  email?: string;
  ai_metadata?: string; // AI analysis and tweet proposals
}

interface Influencer {
  id: number;
  name: string;
  twitter_handle?: string;
  x_handle?: string;
  image_path?: string;
}

interface TweetPipelineItem {
  id: number;
  content: string;
  influencer_id?: number;
  prediction_id?: number;
  custom_images?: string[];
  screenshot_path?: string;
  status: 'draft' | 'ready' | 'scheduled' | 'posted' | 'failed';
  scheduled_time?: string;
  posted_at?: string;
  ai_metadata?: string;
  engagement_data?: string;
  created_at: string;
  updated_at: string;
  influencer_name?: string;
  influencer_image_path?: string;
}

// Styled components
const Container = styled.div`
  padding: 20px;
  background-color: ${props => props.theme.background};
  min-height: 100vh;
  
  @media (max-width: 768px) {
    padding: 10px;
  }
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid ${props => props.theme.border};
`;

const Title = styled.h1`
  font-size: 24px;
  color: ${props => props.theme.text};
`;

const LogoutButton = styled.button`
  padding: 8px 16px;
  background-color: transparent;
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  color: ${props => props.theme.text};
  cursor: pointer;
  
  &:hover {
    background-color: ${props => props.theme.surface};
  }
`;

const Tabs = styled.div`
  display: flex;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin-bottom: 15px;
    padding-bottom: 5px;
  }
`;

const Tab = styled.div<{ active: boolean }>`
  padding: 10px 20px;
  cursor: pointer;
  border-bottom: 2px solid ${props => props.active ? props.theme.primary : 'transparent'};
  color: ${props => props.active ? props.theme.primary : props.theme.text};
  font-weight: ${props => props.active ? 'bold' : 'normal'};
  
  &:hover {
    color: ${props => props.theme.primary};
  }
  
  @media (max-width: 768px) {
    padding: 12px 16px;
    min-width: max-content;
    white-space: nowrap;
    font-size: 14px;
    touch-action: manipulation;
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border-radius: 8px;
  background-color: ${props => props.theme.surface};
  
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: ${props => props.theme.surface};
  min-width: 800px; /* Prevent table from becoming too narrow */
  
  @media (max-width: 768px) {
    min-width: 600px;
  }
  
  @media (max-width: 480px) {
    min-width: 100%;
    font-size: 14px;
  }
`;

const TableHeader = styled.th`
  text-align: left;
  padding: 12px 15px;
  background-color: ${props => props.theme.surfaceDark}; // Now defined in theme
  color: ${props => props.theme.text};
  font-weight: bold;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 15px;
  background-color: ${props => props.theme.surfaceDark};
  color: ${props => props.theme.text};
  font-weight: bold;
  
  @media (max-width: 768px) {
    padding: 8px 6px;
    font-size: 11px;
  }
`;

const Td = styled.td`
  padding: 10px 15px;
  border-top: 1px solid ${props => props.theme.border};
  color: ${props => props.theme.text};
  
  @media (max-width: 768px) {
    padding: 8px 6px;
    font-size: 11px;
  }
`;

const Status = styled.span<{ status: string }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  
  background-color: ${props => {
    switch(props.status) {
      case 'pending': return 'rgba(255, 193, 7, 0.2)';
      case 'approved': return 'rgba(76, 175, 80, 0.2)';
      case 'rejected': return 'rgba(244, 67, 54, 0.2)';
      default: return 'transparent';
    }
  }};
  
  color: ${props => {
    switch(props.status) {
      case 'pending': return '#ffc107';
      case 'approved': return '#4caf50';
      case 'rejected': return '#f44336';
      default: return props.theme.text;
    }
  }};
`;

const PredictionActionButton = styled.button`
  padding: 6px 12px;
  margin-right: 8px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  
  &.approve {
    background-color: rgba(76, 175, 80, 0.2);
    color: #4caf50;
    
    &:hover {
      background-color: rgba(76, 175, 80, 0.3);
    }
  }
  
  &.reject {
    background-color: rgba(244, 67, 54, 0.2);
    color: #f44336;
    
    &:hover {
      background-color: rgba(244, 67, 54, 0.3);
    }
  }
  
  &.view {
    background-color: rgba(33, 150, 243, 0.2);
    color: #2196f3;
    
    &:hover {
      background-color: rgba(33, 150, 243, 0.3);
    }
  }
  
  @media (max-width: 768px) {
    padding: 10px 16px;
    margin: 5px 5px 5px 0;
    font-size: 14px;
    min-height: 44px;
    touch-action: manipulation;
    
    /* iOS Safari specific button fixes */
    -webkit-appearance: none;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -webkit-tap-highlight-color: transparent;
    border-radius: 6px;
    
    &:focus {
      outline: none;
      -webkit-tap-highlight-color: transparent;
    }
    
    &:active {
      -webkit-tap-highlight-color: transparent;
      transform: scale(0.98);
    }
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  
  /* iOS Safari specific fixes */
  -webkit-transform: translate3d(0, 0, 0);
  transform: translate3d(0, 0, 0);
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  
  /* Prevent iOS Safari scrolling and viewport issues */
  @supports (-webkit-touch-callout: none) {
    position: fixed !important;
    -webkit-overflow-scrolling: touch;
    -webkit-user-select: none;
    user-select: none;
    -webkit-touch-callout: none;
    touch-action: none;
    overscroll-behavior: none;
    
    /* Lock the viewport to prevent Safari from trying to scroll */
    height: 100vh;
    height: 100dvh; /* Dynamic viewport height for newer iOS versions */
  }
`;

const ModalContent = styled.div`
  background-color: ${props => props.theme.surface};
  padding: 20px;
  border-radius: 8px;
  width: 800px;
  max-width: 95%;
  max-height: 90vh;
  overflow-y: auto;
  
  /* iOS Safari specific fixes */
  -webkit-transform: translate3d(0, 0, 0);
  transform: translate3d(0, 0, 0);
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  
  @media (max-width: 768px) {
    width: 95%;
    max-width: 95%;
    padding: 15px;
    border-radius: 4px;
    max-height: 95vh;
    
    /* iOS specific mobile fixes */
    @supports (-webkit-touch-callout: none) {
      -webkit-overflow-scrolling: touch;
      position: relative;
      height: auto;
      min-height: 50vh;
    }
  }
`;

// Social Media Posting Styled Components
const SocialMediaSection = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 20px;
  margin: 20px 0;
  color: white;
`;

const SocialMediaHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const SocialMediaTitle = styled.h3`
  margin: 0;
  color: white;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ToggleButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const TweetProposalSection = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
`;

const ProposalGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 10px;
  margin-bottom: 15px;
`;

const ProposalCard = styled.div<{ selected: boolean }>`
  background: ${props => props.selected ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)'};
  border: 2px solid ${props => props.selected ? '#4CAF50' : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 8px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const ProposalStyle = styled.div`
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
  margin-bottom: 8px;
  color: #4CAF50;
`;

const ProposalText = styled.div`
  font-size: 14px;
  line-height: 1.4;
`;

const CustomTweetSection = styled.div`
  margin-bottom: 15px;
`;

const CustomTweetTextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  max-height: 300px;
  padding: 10px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  resize: vertical;
  
  @media (max-width: 768px) {
    min-height: 100px;
    padding: 12px;
    font-size: 16px;
    border-radius: 8px;
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.7);
  }
  
  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
`;

const ImageUploadSection = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
`;

const PasteDropArea = styled.div<{ isDragOver?: boolean; isPasting?: boolean }>`
  border: 2px dashed ${props => 
    props.isPasting ? '#4CAF50' :
    props.isDragOver ? '#2196F3' : 
    'rgba(255, 255, 255, 0.3)'
  };
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 15px;
  text-align: center;
  background: ${props => 
    props.isPasting ? 'rgba(76, 175, 80, 0.1)' :
    props.isDragOver ? 'rgba(33, 150, 243, 0.1)' : 
    'rgba(255, 255, 255, 0.05)'
  };
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
  }
`;

const PasteInstructions = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  margin-bottom: 10px;
  
  .shortcut {
    background: rgba(255, 255, 255, 0.2);
    padding: 2px 6px;
    border-radius: 3px;
    font-family: monospace;
    font-size: 12px;
  }
`;

const PasteIndicator = styled.div<{ show: boolean }>`
  display: ${props => props.show ? 'block' : 'none'};
  color: #4CAF50;
  font-size: 16px;
  font-weight: bold;
  margin-top: 10px;
  animation: ${props => props.show ? 'pulse 1s ease-in-out' : 'none'};
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const ImageUploadButton = styled.button`
  background: #4CAF50;
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 6px;
  cursor: pointer;
  margin-right: 10px;
  
  &:hover {
    background: #45a049;
  }
`;

const ClearImagesButton = styled.button`
  background: #f44336;
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 6px;
  cursor: pointer;
  
  &:hover {
    background: #da190b;
  }
`;

const ImagePreviewGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-top: 15px;
`;

const ImagePreviewColumn = styled.div`
  h5 {
    margin: 0 0 10px 0;
    color: white;
    font-size: 14px;
  }
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 10px;
`;

const ImagePreviewContainer = styled.div`
  position: relative;
  border-radius: 6px;
  overflow: hidden;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 80px;
  object-fit: cover;
  border-radius: 4px;
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: 5px;
  right: 5px;
  background: rgba(244, 67, 54, 0.8);
  border: none;
  border-radius: 3px;
  padding: 2px 6px;
  color: white;
  cursor: pointer;
  font-size: 12px;
`;

const PostingButtonsRow = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
`;

const SocialPostButton = styled.button<{ platform: 'x' | 'nostr' | 'both' }>`
  background: ${props => 
    props.platform === 'x' ? 'linear-gradient(45deg, #1DA1F2, #0d95e8)' :
    props.platform === 'nostr' ? 'linear-gradient(45deg, #8B5CF6, #7C3AED)' :
    'linear-gradient(45deg, #4CAF50, #45a049)'};
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: bold;
  flex: 1;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const SocialResultSection = styled.div<{ success: boolean }>`
  background: ${props => props.success ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)'};
  border: 1px solid ${props => props.success ? '#4CAF50' : '#f44336'};
  border-radius: 6px;
  padding: 10px;
  margin-top: 10px;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid ${props => props.theme.border};
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  color: ${props => props.theme.text};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: ${props => props.theme.text};
`;

const FormGroup = styled.div`
  margin-bottom: 15px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 5px;
  color: ${props => props.theme.text};
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  background-color: ${props => props.theme.inputBackground}; // Now defined in theme
  color: ${props => props.theme.text};
  resize: vertical;
  min-height: 100px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  background-color: ${props => props.theme.inputBackground}; // Now defined in theme
  color: ${props => props.theme.text};
`;

const Select = styled.select`
  width: 100%;
  padding: 10px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  background-color: ${props => props.theme.inputBackground};
  color: ${props => props.theme.text};
`;

const InfoBox = styled.div`
  padding: 10px;
  margin-bottom: 15px;
  border-radius: 4px;
  background-color: rgba(33, 150, 243, 0.1);
  border: 1px solid rgba(33, 150, 243, 0.3);
  color: ${props => props.theme.text};
`;

const AdminDashboard: React.FC = () => {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [predictions, setPredictions] = useState<any[]>([]);
  const [influencers, setInfluencers] = useState<Influencer[]>([]);
  
  // Curated tweets state
  const [selectedInfluencer, setSelectedInfluencer] = useState<number | null>(null);
  const [curationCount, setCurationCount] = useState<number>(50);
  const [curationMonths, setCurationMonths] = useState<number>(6);
  const [isCurating, setIsCurating] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [curatedQueue, setCuratedQueue] = useState<any[]>([]);
  const [queueStats, setQueueStats] = useState<any>(null);
  
  // New state for two-step approach
  const [realTweets, setRealTweets] = useState<any[]>([]);
  const [selectedTweetIds, setSelectedTweetIds] = useState<Set<string>>(new Set());
  const [isLoadingRealTweets, setIsLoadingRealTweets] = useState<boolean>(false);
  const [step, setStep] = useState<'select' | 'review' | 'analyze'>('select');
  const [cacheKey, setCacheKey] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'pending' | 'approved' | 'rejected' | 'all' | 'x-com' | 'tweet-pipeline' | 'tweet-extraction' | 'prediction-manager' | 'link-submissions' | 'bulk-tweets' | 'curated-tweets'>('pending');

  // Default theme for components that need it
  const theme = {
    surface: '#2d3748',
    background: '#1a202c', 
    text: '#e2e8f0',
    subText: '#a0aec0',
    primary: '#3182ce',
    border: '#4a5568',
    hoverBackground: '#374151',
    surfaceDark: '#1a202c',
    link: '#3182ce',
    accent: '#1da1f2',
    activeBackground: '#4a5568',
    hoverActiveBackground: '#2d3748',
    activeBorder: '#3182ce',
    hoverBorder: '#3182ce',
    inputBackground: '#2d3748',
    successBackground: '#065f46',
    successText: '#10b981',
    pendingBackground: '#92400e',
    pendingText: '#f59e0b',
    failedBackground: '#7f1d1d',
    failedText: '#ef4444',
    defaultBackground: '#374151',
    defaultText: '#9ca3af',
    errorText: '#ef4444',
    tableHeaderBackground: '#374151',
    chartText: '#a0aec0',
    chartGrid: '#4a5568',
    tweet: {
      nameColor: '#e7e9ea',
      handleColor: '#71767b',
      textColor: '#e7e9ea',
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      borderColor: 'rgba(83, 100, 113, 0.3)'
    },
    mobile: '768px',
    tablet: '1024px',
    desktop: '1025px',
    livePriceLine: '#3182ce'
  };
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
  const [modalMode, setModalMode] = useState<'view' | 'approve' | 'reject'>('view');
  const [comment, setComment] = useState('');
  // Removed predictionMade state - now using editedData.prediction_made
  const [selectedInfluencerId, setSelectedInfluencerId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [startDate, setStartDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState<string>('');
  
  // NEW: State for editing all fields
  const [editedData, setEditedData] = useState<Partial<Submission>>({});
  
  // Tweet Pipeline Edit States
  const [editingTweet, setEditingTweet] = useState<number | null>(null);
  const [editedTweetData, setEditedTweetData] = useState<Partial<TweetPipelineItem>>({});
  
  // Social Media Posting States
  const [showSocialMediaSection, setShowSocialMediaSection] = useState(true);
  const [selectedTweetProposal, setSelectedTweetProposal] = useState<string>('');
  const [customTweetText, setCustomTweetText] = useState<string>('');
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);

  const [isPostingToX, setIsPostingToX] = useState(false);
  const [isPostingToNostr, setIsPostingToNostr] = useState(false);
  const [socialMediaResult, setSocialMediaResult] = useState<any>(null);
  const [isRegeneratingAI, setIsRegeneratingAI] = useState(false);
  const [saveToTweetPipeline, setSaveToTweetPipeline] = useState(false);
  const [isSavingToPipeline, setIsSavingToPipeline] = useState(false);
  
  // Tweet Pipeline States
  const [tweetPipelineItems, setTweetPipelineItems] = useState<TweetPipelineItem[]>([]);
  const [selectedTweetPipeline, setSelectedTweetPipeline] = useState<TweetPipelineItem | null>(null);
  const [tweetPipelineModalMode, setTweetPipelineModalMode] = useState<'view' | 'create' | 'edit'>('view');
  const [tweetContent, setTweetContent] = useState('');
  const [tweetInfluencerId, setTweetInfluencerId] = useState<number | null>(null);
  const [tweetPredictionId, setTweetPredictionId] = useState<number | null>(null);
  const [tweetScheduledFor, setTweetScheduledFor] = useState<string>('');
  const [tweetAIContext, setTweetAIContext] = useState<string>('');
  const [tweetContextType, setTweetContextType] = useState<string>('');
  const [tweetImages, setTweetImages] = useState<File[]>([]);
  const [tweetImagePreviews, setTweetImagePreviews] = useState<string[]>([]);
  const [isLoadingTweets, setIsLoadingTweets] = useState(false);
  const [isPostingTweet, setIsPostingTweet] = useState(false);
  

  
  // AI Generation state
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [isGeneratingScreenshot, setIsGeneratingScreenshot] = useState(false);
  
  // Paste and drag states for social media section
  const [isPastingSocial, setIsPastingSocial] = useState(false);
  const [isDragOverSocial, setIsDragOverSocial] = useState(false);
  
  // Paste and drag states for tweet pipeline section
  const [isPastingTweet, setIsPastingTweet] = useState(false);
  const [isDragOverTweet, setIsDragOverTweet] = useState(false);
  
  // Fetch tweet from URL state
  const [isFetchingTweet, setIsFetchingTweet] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const socialPasteAreaRef = useRef<HTMLDivElement>(null);
  const tweetPasteAreaRef = useRef<HTMLDivElement>(null);
  
  // Bulk Tweet States
  const [bulkTweets, setBulkTweets] = useState<any[]>([]);
  const [selectedInfluencerForBulk, setSelectedInfluencerForBulk] = useState<number | null>(null);
  const [bulkStartDate, setBulkStartDate] = useState<string>('2024-01-01');
  const [bulkEndDate, setBulkEndDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [maxTweets, setMaxTweets] = useState<number>(1000);
  const [bulkDownloadStatus, setBulkDownloadStatus] = useState<string>('');
  const [activeBatches, setActiveBatches] = useState<any[]>([]);
  const [bulkAnalytics, setBulkAnalytics] = useState<any>(null);
  const [isDownloadingBulk, setIsDownloadingBulk] = useState<boolean>(false);
  
  // Social Media Helper Functions
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    const newImages = [...selectedImages, ...files].slice(0, 4); // Max 4 images
    setSelectedImages(newImages);
    
    // Generate previews
    const newPreviews: string[] = [];
    
    newImages.forEach((file, index) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imgSrc = e.target?.result as string;
        newPreviews[index] = imgSrc;
        
        if (newPreviews.length === newImages.length) {
          setImagePreviews([...newPreviews]);
        }
      };
      reader.readAsDataURL(file);
    });
  };



  const removeImage = (index: number) => {
    const newImages = selectedImages.filter((_, i) => i !== index);
    const newPreviews = imagePreviews.filter((_, i) => i !== index);
    
    setSelectedImages(newImages);
    setImagePreviews(newPreviews);
  };

  // Handle paste events for social media section
  const handleSocialPaste = async (e: ClipboardEvent) => {
    e.preventDefault();
    const items = e.clipboardData?.items;
    if (!items) return;

    setIsPastingSocial(true);
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.indexOf('image') !== -1) {
        const file = item.getAsFile();
        if (file && selectedImages.length < 4) {
          // Create a new file with a proper name
          const pastedFile = new File([file], `pasted-image-${Date.now()}.${file.type.split('/')[1]}`, {
            type: file.type
          });
          
          const newImages = [...selectedImages, pastedFile];
          setSelectedImages(newImages);
          
          // Generate preview
          const reader = new FileReader();
          reader.onload = (e) => {
            const imgSrc = e.target?.result as string;
            const newPreviews = [...imagePreviews, imgSrc];
            setImagePreviews(newPreviews);
          };
          reader.readAsDataURL(pastedFile);
          
          break; // Only paste one image at a time
        }
      }
    }
    
    setTimeout(() => setIsPastingSocial(false), 1000);
  };

  // Handle paste events for tweet pipeline section  
  const handleTweetPaste = async (e: ClipboardEvent) => {
    e.preventDefault();
    const items = e.clipboardData?.items;
    if (!items) return;

    setIsPastingTweet(true);
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.indexOf('image') !== -1) {
        const file = item.getAsFile();
        if (file && tweetImages.length < 4) {
          // Create a new file with a proper name
          const pastedFile = new File([file], `pasted-image-${Date.now()}.${file.type.split('/')[1]}`, {
            type: file.type
          });
          
          // Add image directly without cropping
          const newImages = [...tweetImages, pastedFile];
          setTweetImages(newImages);
          setIsPastingTweet(true);
          setTimeout(() => setIsPastingTweet(false), 2000);
          
          break; // Only paste one image at a time
        }
      }
    }
    
    setTimeout(() => setIsPastingTweet(false), 1000);
  };

  // Drag and drop handlers for social media section
  const handleSocialDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOverSocial(true);
  };

  const handleSocialDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOverSocial(false);
  };

  const handleSocialDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOverSocial(false);
    
    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length > 0 && selectedImages.length < 4) {
      const file = imageFiles[0]; // Take first image
      const newImages = [...selectedImages, file];
      setSelectedImages(newImages);
      
      // Generate preview
      const reader = new FileReader();
      reader.onload = (e) => {
        const imgSrc = e.target?.result as string;
        const newPreviews = [...imagePreviews, imgSrc];
        setImagePreviews(newPreviews);
      };
      reader.readAsDataURL(file);
    }
  };

  // Drag and drop handlers for tweet pipeline section
  const handleTweetDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOverTweet(true);
  };

  const handleTweetDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOverTweet(false);
  };

  const handleTweetDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOverTweet(false);
    
    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length > 0 && tweetImages.length < 4) {
      const file = imageFiles[0]; // Take first image
      
      // Add image directly without cropping
      const newImages = [...tweetImages, file];
      setTweetImages(newImages);
    }
  };

  const clearImages = () => {
    setSelectedImages([]);
    setImagePreviews([]);
  };

  const getTweetProposals = () => {
    if (!selectedSubmission?.ai_metadata) return [];
    
    try {
      const metadata = JSON.parse(selectedSubmission.ai_metadata);
      return metadata.tweet_proposals || [];
    } catch (e) {
      return [];
    }
  };

  const selectTweetProposal = (proposal: any) => {
    setSelectedTweetProposal(proposal.text);
    setCustomTweetText(proposal.text);
  };



  const postToSocialMedia = async (platform: 'x' | 'nostr' | 'both') => {
    if (!customTweetText.trim()) {
      alert('Please enter tweet text');
      return;
    }

    if (platform === 'x' || platform === 'both') setIsPostingToX(true);
    if (platform === 'nostr' || platform === 'both') setIsPostingToNostr(true);
    
    try {
      const results: any = {};
      
      // Post to X.com
      if (platform === 'x' || platform === 'both') {
        try {
          let xResponse;
          
          if (selectedImages.length > 0) {
            const formData = new FormData();
            formData.append('text', customTweetText);
            selectedImages.forEach((image) => {
              formData.append('images', image);
            });
            
            xResponse = await api.post('/api/x/tweet-with-images', formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          } else {
            xResponse = await api.post('/api/x/tweet', { text: customTweetText });
          }
          
          results.x = xResponse.data;
        } catch (error: any) {
          results.x = { 
            success: false, 
            error: error.response?.data?.error || 'Failed to post to X.com'
          };
        }
      }
      
      // Post to Nostr
      if (platform === 'nostr' || platform === 'both') {
        try {
          let nostrResponse;
          
          if (selectedImages.length > 0) {
            const formData = new FormData();
            formData.append('content', customTweetText);
            selectedImages.forEach((image) => {
              formData.append('images', image);
            });
            
            nostrResponse = await api.post('/api/nostr/post', formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          } else {
            nostrResponse = await api.post('/api/nostr/post', { content: customTweetText });
          }
          
          results.nostr = nostrResponse.data;
        } catch (error: any) {
          results.nostr = { 
            success: false, 
            error: error.response?.data?.error || 'Failed to post to Nostr'
          };
        }
      }
      
      setSocialMediaResult(results);
      
      // If successful, clear form and mark as approved
      if ((results.x?.success || platform === 'nostr') && (results.nostr?.success || platform === 'x')) {
        setCustomTweetText('');
        clearImages();
        
        // Automatically approve the submission
        const approvalData = {
          influencer_id: selectedInfluencerId,
          admin_comment: `Posted to social media: ${platform}`,
          prediction_made: editedData.prediction_made || selectedSubmission?.prediction_made || new Date().toISOString().split('T')[0]
        };
        
        if (selectedSubmission) {
          await api.put(`/api/admin/prediction_submissions/${selectedSubmission.id}/approve`, approvalData);
        }
        
        // Refresh submissions list and close modal
        window.location.reload(); // Simple refresh for now
        closeModal();
      }
      
    } finally {
      setIsPostingToX(false);
      setIsPostingToNostr(false);
    }
  };
  const [isEditMode, setIsEditMode] = useState(false);
  
  const navigate = useNavigate();
  
  // Setup unified API client
  const api = axios.create({
    baseURL: apiBase
  });

  // Fetch tweet content from source URL
  const fetchTweetFromUrl = async () => {
    const sourceUrl = editedData.source_url || selectedSubmission?.source_url;
    if (!sourceUrl || !selectedSubmission) {
      alert('❌ No source URL provided');
      return;
    }

    setIsFetchingTweet(true);
    try {
      const response = await api.post('/api/admin/fetch-tweet-content', {
        url: sourceUrl
      });

      if (response.data.success && response.data.tweet) {
        const tweet = response.data.tweet;
        
        // Update editedData with fetched tweet info
        const newEditedData = {
          ...editedData,
          notes: tweet.text || tweet.content,
          expert_name: editedData.expert_name || tweet.author?.name || tweet.author_name,
          twitter_handle: editedData.twitter_handle || tweet.author?.username || tweet.author_username
        };
        setEditedData(newEditedData);
        
        // Also update the selectedSubmission for display
        setSelectedSubmission({
          ...selectedSubmission,
          notes: tweet.text || tweet.content,
          expert_name: newEditedData.expert_name || selectedSubmission.expert_name,
          twitter_handle: newEditedData.twitter_handle || selectedSubmission.twitter_handle
        });
        
        alert(`✅ Tweet fetched! Now click "Analyze with AI" to extract prediction data.`);
      } else {
        throw new Error(response.data.error || 'Failed to fetch tweet');
      }
    } catch (error: any) {
      console.error('Error fetching tweet:', error);
      alert(`❌ Failed to fetch tweet: ${error.response?.data?.error || error.message}`);
    } finally {
      setIsFetchingTweet(false);
    }
  };

  // Analyze tweet with AI and extract prediction data
  const analyzeWithAI = async () => {
    if (!selectedSubmission) return;
    
    const tweetText = editedData.notes || selectedSubmission.notes;
    if (!tweetText || tweetText.trim().length < 10) {
      alert('❌ Please enter or fetch the tweet text first (in Quote/Notes field)');
      return;
    }

    setIsRegeneratingAI(true);
    try {
      const response = await api.post(`/api/admin/prediction_submissions/${selectedSubmission.id}/regenerate-ai`, {
        tweet_text: tweetText,
        author_name: editedData.expert_name || selectedSubmission.expert_name,
        author_handle: editedData.twitter_handle || selectedSubmission.twitter_handle
      });

      if (response.data.success) {
        const aiAnalysis = response.data.ai_analysis;
        
        // Auto-fill prediction fields from AI analysis
        const newEditedData = {
          ...editedData,
          price_prediction: aiAnalysis.price_prediction || editedData.price_prediction,
          price_target: aiAnalysis.target_price || aiAnalysis.price_target || editedData.price_target,
          price_min: aiAnalysis.price_min || editedData.price_min,
          price_max: aiAnalysis.price_max || editedData.price_max,
          prediction_type: aiAnalysis.prediction_type || editedData.prediction_type,
          target_time: aiAnalysis.target_date || editedData.target_time
        };
        setEditedData(newEditedData);
        
        // Update the selected submission with new AI metadata
        const updatedSubmission = { 
          ...selectedSubmission, 
          ...newEditedData,
          ai_metadata: JSON.stringify(aiAnalysis) 
        };
        setSelectedSubmission(updatedSubmission);
        
        // Clear any previously selected proposal
        setSelectedTweetProposal('');
        setCustomTweetText('');
        
        alert(`✅ AI analysis complete!\n\nDetected: ${aiAnalysis.prediction_type || 'target'} prediction\nPrice: $${aiAnalysis.target_price || aiAnalysis.price_target || 'N/A'}\n\nFields have been auto-filled. Review and save.`);
      } else {
        throw new Error(response.data.error || 'Failed to analyze with AI');
      }
    } catch (error: any) {
      console.error('Error analyzing with AI:', error);
      alert(`❌ Failed to analyze: ${error.response?.data?.error || error.message}`);
    } finally {
      setIsRegeneratingAI(false);
    }
  };

  // Legacy regenerate AI proposals function (for tweet proposals only)
  const regenerateAIProposals = async () => {
    if (!selectedSubmission) return;
    
    const tweetText = editedData.notes || selectedSubmission.notes || selectedSubmission.price_prediction;
    if (!tweetText) {
      alert('❌ No tweet text available. Please fetch or enter the tweet content first.');
      return;
    }

    setIsRegeneratingAI(true);
    try {
      const response = await api.post(`/api/admin/prediction_submissions/${selectedSubmission.id}/regenerate-ai`, {
        tweet_text: tweetText,
        author_name: editedData.expert_name || selectedSubmission.expert_name,
        author_handle: editedData.twitter_handle || selectedSubmission.twitter_handle
      });

      if (response.data.success) {
        // Update the selected submission with new AI metadata
        const updatedSubmission = { 
          ...selectedSubmission, 
          ai_metadata: JSON.stringify(response.data.ai_analysis) 
        };
        setSelectedSubmission(updatedSubmission);
        
        // Clear any previously selected proposal
        setSelectedTweetProposal('');
        setCustomTweetText('');
        
        alert('✅ AI proposals regenerated successfully!');
      } else {
        throw new Error(response.data.error || 'Failed to regenerate AI proposals');
      }
    } catch (error: any) {
      console.error('Error regenerating AI proposals:', error);
      alert(`❌ Failed to regenerate AI proposals: ${error.response?.data?.error || error.message}`);
    } finally {
      setIsRegeneratingAI(false);
    }
  };
  
  // Check authentication in a simpler way
  useEffect(() => {
    const loggedIn = localStorage.getItem('adminLoggedIn');
    if (loggedIn !== 'true') {
      navigate('/admin/login');
    }
  }, [navigate]);
  
  // Load submissions based on active tab
  useEffect(() => {
    const fetchSubmissions = async () => {
      try {
        setLoading(true);
        setError('');
        
        let url = `/api/admin/prediction_submissions`;
        if (activeTab !== 'all') {
          url += `?status=${activeTab}`;
        }
        
        
        const response = await api.get(url);
        
        // Deduplicate submissions by ID to fix React key warning
        const uniqueSubmissions = response.data.reduce((acc: any[], current: any) => {
          const existingIndex = acc.findIndex(item => item.id === current.id);
          if (existingIndex === -1) {
            acc.push(current);
          }
          return acc;
        }, []);
        
        setSubmissions(uniqueSubmissions);
      } catch (err: any) {
        console.error('Error fetching submissions:', err);
        setError('Failed to load submissions. Please make sure the backend server is running.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchSubmissions();
  }, [activeTab]);
  
  // Load influencers for reference
  useEffect(() => {
    const fetchInfluencers = async () => {
      try {
        const response = await api.get('/api/admin/influencers');
        setInfluencers(response.data);
        console.log('Loaded influencers:', response.data.length);
      } catch (err) {
        console.error('Error fetching influencers:', err);
        setInfluencers([]);
      }
    };
    
    fetchInfluencers();
  }, []);
  
  // Load predictions for tweet pipeline linking
  useEffect(() => {
    const fetchPredictions = async () => {
      try {
        const response = await api.get('/api/admin/predictions');
        setPredictions(response.data);
        console.log('Loaded predictions:', response.data.length);
      } catch (err) {
        console.error('Error fetching predictions:', err);
        setPredictions([]);
      }
    };
    
    fetchPredictions();
  }, []);
  
  const handleLogout = () => {
    localStorage.removeItem('adminLoggedIn');
    localStorage.removeItem('adminUsername');
    navigate('/admin/login');
  };

  const handleDeletePrediction = async (submission: Submission) => {
    const confirmMessage = `⚠️ WARNING: This will permanently delete the entire prediction entry for "${submission.expert_name}".\n\nThis action will remove:\n• The submission from prediction_submissions table\n• The corresponding entry from predictions table (if it exists)\n• All associated data\n\nThis cannot be undone. Are you sure?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    const finalConfirmation = `🚨 FINAL CONFIRMATION\n\nYou are about to PERMANENTLY DELETE:\n\nExpert: ${submission.expert_name}\nSubmission ID: ${submission.id}\nStatus: ${submission.status}\n\nClick OK to proceed, or Cancel to abort.`;
    
    if (!window.confirm(finalConfirmation)) {
      return;
    }

    try {
      console.log(`🗑️ Attempting to delete prediction - Submission ID: ${submission.id}, Expert: ${submission.expert_name}`);
      
      const response = await axios.delete(`${apiBase}/api/admin/predictions/${submission.id}/delete`);
      
      if (response.data.success) {
        alert(`✅ Successfully deleted prediction for ${submission.expert_name}!\n\nDeleted:\n• Submission ID: ${submission.id}\n• Prediction entries: ${response.data.deletedPredictions || 0}\n• Cleanup completed`);
        
        // Refresh the submissions list by updating active tab to trigger useEffect
        const currentTab = activeTab;
        setActiveTab('all');
        setTimeout(() => setActiveTab(currentTab), 100);
      } else {
        alert(`❌ Failed to delete prediction: ${response.data.message}`);
      }
    } catch (error: any) {
      console.error('Delete prediction error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      alert(`❌ Failed to delete prediction for ${submission.expert_name}: ${errorMessage}`);
    }
  };
  
  const openModal = (submission: Submission, mode: 'view' | 'approve' | 'reject') => {
    setSelectedSubmission(submission);
    setModalMode(mode);
    setComment(submission.admin_comment || '');
    
    // Initialize edited data with all submission data
    setEditedData({ ...submission });
    
    // Prediction made date is now handled through editedData initialization above
    
    // If the submission has a matching influencer, select it
    if (submission.existing_influencer_id) {
      setSelectedInfluencerId(submission.existing_influencer_id);
    } else {
      setSelectedInfluencerId(null);
    }
    
    // Set the default end date to the target date
    if (submission.target_time) {
      const formattedDate = new Date(submission.target_time).toISOString().split('T')[0];
      setEndDate(formattedDate);
    }
  };
  
  const closeModal = () => {
    setSelectedSubmission(null);
    setComment('');
    setSelectedInfluencerId(null);
    setStartDate(new Date().toISOString().split('T')[0]);
    setEndDate('');
    setEditedData({});
    setIsEditMode(false);
  };
  
  // NEW: Helper function to parse price strings (handles commas, dots, etc.)
  const parsePrice = (value: string | number | null | undefined): number | null => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number') return value;
    
    let cleanedValue = String(value).replace(/[$\s]/g, ''); // Remove $ and spaces
    
    // Handle European format (1.000.000,50) vs US format (1,000,000.50)
    // Check if we have both dots and commas to determine format
    const hasDot = cleanedValue.includes('.');
    const hasComma = cleanedValue.includes(',');
    
    if (hasDot && hasComma) {
      // Both present - check which comes last (that's the decimal separator)
      const lastDot = cleanedValue.lastIndexOf('.');
      const lastComma = cleanedValue.lastIndexOf(',');
      
      if (lastComma > lastDot) {
        // European format: 1.000.000,50 - dots are thousands, comma is decimal
        cleanedValue = cleanedValue.replace(/\./g, '').replace(',', '.');
      } else {
        // US format: 1,000,000.50 - commas are thousands, dot is decimal
        cleanedValue = cleanedValue.replace(/,/g, '');
      }
    } else if (hasComma && !hasDot) {
      // Only comma - could be European decimal or US thousands
      // If comma is followed by exactly 2 digits at end, treat as decimal
      if (/,\d{2}$/.test(cleanedValue)) {
        cleanedValue = cleanedValue.replace(',', '.');
      } else {
        cleanedValue = cleanedValue.replace(/,/g, '');
      }
    } else if (hasDot && !hasComma) {
      // Only dot - could be US decimal or European thousands
      // If dot is followed by exactly 2-3 digits at end, treat as decimal (keep as is)
      // Otherwise, if followed by exactly 3 digits not at end, treat as thousands separator
      if (!/\.\d{2,3}$/.test(cleanedValue) && /\.\d{3}/.test(cleanedValue)) {
        cleanedValue = cleanedValue.replace(/\./g, '');
      }
      // Otherwise keep the dot as decimal separator
    }
    
    const parsed = parseFloat(cleanedValue);
    return isNaN(parsed) ? null : Math.round(parsed); // Round to nearest integer for prices
  };
  
  // NEW: Helper function to format price for display
  const formatPriceDisplay = (value: number | string | null | undefined): string => {
    if (value === null || value === undefined || value === '') return '';
    const num = typeof value === 'number' ? value : parsePrice(value);
    return num ? num.toString() : '';
  };
  
  // NEW: Handle field changes in edit mode
  const handleFieldChange = (field: keyof Submission, value: any) => {
    setEditedData(prev => ({ ...prev, [field]: value }));
  };
  
  // NEW: Toggle edit mode
  const toggleEditMode = () => {
    if (!isEditMode && selectedSubmission) {
      // Initialize edited data with current submission data
      setEditedData({ ...selectedSubmission });
    }
    setIsEditMode(!isEditMode);
  };
  
  // NEW: Save edited data
  const saveEditedData = async () => {
    if (!selectedSubmission || !editedData) return;
    
    try {
      
      await api.put(`/api/admin/prediction_submissions/${selectedSubmission.id}/update`, editedData);
      
      // Update the submission in state
      setSubmissions(prev => prev.map(sub => 
        sub.id === selectedSubmission.id ? { ...sub, ...editedData } : sub
      ));
      
      // Update selected submission
      setSelectedSubmission({ ...selectedSubmission, ...editedData });
      
      setIsEditMode(false);
      alert('Data saved successfully!');
    } catch (err) {
      console.error('Error saving edited data:', err);
      alert('Error saving data. Please try again.');
    }
  };
  
  // Bulk Tweet Functions
  const startBulkDownload = async () => {
    if (!selectedInfluencerForBulk) {
      alert('Please select an influencer');
      return;
    }
    
    setIsDownloadingBulk(true);
    setBulkDownloadStatus('Starting download...');
    
    try {
      const response = await axios.post(`${apiBase}/api/bulk-tweets/start`, {
        influencer_id: selectedInfluencerForBulk,
        start_date: bulkStartDate,
        end_date: bulkEndDate,
        max_tweets: maxTweets
      });
      
      if (response.data.success) {
        setBulkDownloadStatus(`Download started! Batch ID: ${response.data.batch_id}`);
        loadActiveBatches();
      } else {
        setBulkDownloadStatus('Failed to start download');
      }
    } catch (error) {
      console.error('Error starting bulk download:', error);
      setBulkDownloadStatus('Error starting download');
    } finally {
      setIsDownloadingBulk(false);
    }
  };

  const loadActiveBatches = async () => {
    try {
      // For now, we'll simulate loading batches since we don't have a "list all batches" endpoint
      setBulkDownloadStatus('Checking for active batches...');
    } catch (error) {
      console.error('Error loading batches:', error);
    }
  };

  const searchBulkTweets = async (searchTerm: string) => {
    try {
      const response = await axios.post(`${apiBase}/api/bulk-tweets/search`, {
        search_term: searchTerm,
        filters: {
          has_prediction: true,
          influencer_id: selectedInfluencerForBulk,
          limit: 100
        }
      });
      
      if (response.data.results) {
        setBulkTweets(response.data.results);
      }
    } catch (error) {
      console.error('Error searching tweets:', error);
    }
  };

  const getInfluencerBulkStats = async (influencerId: number) => {
    try {
      const response = await axios.get(`${apiBase}/api/bulk-tweets/influencer/${influencerId}/stats`);
      setBulkAnalytics(response.data.stats);
    } catch (error) {
      console.error('Error getting influencer stats:', error);
    }
  };
  
  // Curated Tweets Functions
  const fetchCuratedQueue = async () => {
    try {
      const response = await fetch(`${apiBase}/api/admin/curated-queue-status`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setCuratedQueue(data.queue || []);
        setQueueStats(data.stats || {});
      }
    } catch (error) {
      console.error('Error fetching curated queue:', error);
    }
  };
  
  // Load curated queue on component mount and when tab changes to curated-tweets
  useEffect(() => {
    if (activeTab === 'curated-tweets') {
      fetchCuratedQueue();
    }
  }, [activeTab]);

  // New functions for two-step approach
  const fetchRealTweets = async () => {
    if (!selectedInfluencer) return;
    
    setIsLoadingRealTweets(true);
    try {
      const response = await fetch(`${apiBase}/api/admin/fetch-real-tweets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          influencerId: selectedInfluencer,
          maxResults: curationCount * 3, // Get more tweets to choose from
          months: curationMonths
        })
      });
      const result = await response.json();
      if (result.success) {
        setRealTweets(result.tweets || []);
        setCacheKey(result.cacheKey || null);
        setStep('review');
        alert(`✅ Fetched ${result.count} real tweets (Cost: ${result.costEstimate})`);
      } else {
        alert(`❌ Error: ${result.error}`);
      }
    } catch (error) {
      alert(`❌ Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoadingRealTweets(false);
    }
  };

  const toggleTweetSelection = (tweetId: string) => {
    const newSelection = new Set(selectedTweetIds);
    if (newSelection.has(tweetId)) {
      newSelection.delete(tweetId);
    } else {
      newSelection.add(tweetId);
    }
    setSelectedTweetIds(newSelection);
  };

  const analyzeSelectedTweets = async () => {
    if (selectedTweetIds.size === 0) {
      alert('Please select at least one tweet to analyze');
      return;
    }

    setIsCurating(true);
    try {
      const response = await fetch(`${apiBase}/api/admin/analyze-selected-tweets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          influencerId: selectedInfluencer,
          selectedTweetIds: Array.from(selectedTweetIds),
          cacheKey: cacheKey,
          count: Math.min(selectedTweetIds.size, 10)
        })
      });
      const result = await response.json();
      if (result.success) {
        alert(`✅ ${result.message} (Actual cost: ${result.actualCost})`);
        setStep('analyze');
        // Refresh the curated queue to show new results
        fetchCuratedQueue();
      } else {
        alert(`❌ Error: ${result.error}`);
      }
    } catch (error) {
      alert(`❌ Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsCurating(false);
    }
  };
  
  const handleApprove = async () => {
    if (!selectedSubmission) return;
    
    
    try {
      // Show immediate feedback to prevent user from clicking again
      const originalText = 'Approving...';
      
      
      const approvalData = {
        influencer_id: selectedInfluencerId,
        admin_comment: comment,
        start_date: startDate,
        end_date: endDate,
        price_min: selectedSubmission.price_min,
        price_max: selectedSubmission.price_max,
        price_target: selectedSubmission.price_target,
        prediction_type: selectedSubmission.prediction_type,
        price_prediction: selectedSubmission.price_prediction
      };
      
      
      const response = await api.post(`/api/admin/prediction_submissions/${selectedSubmission.id}/approve`, approvalData);
      
      // If user wants to save to tweet pipeline, do that now
      if (saveToTweetPipeline && customTweetText.trim()) {
        await saveTweetToPipeline(response.data.prediction_id || selectedSubmission.id);
      }
      
      // Refresh the submissions list  
      const refreshResponse = await api.get(`/api/admin/prediction_submissions?status=${activeTab}`);
      setSubmissions(refreshResponse.data);
        
      alert('✅ Submission approved successfully! Switch to the "Approved" tab to see it.');
      closeModal();
      
    } catch (err) {
      console.error('❌ Error approving submission:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      alert('❌ Error approving submission: ' + errorMessage);
    }
  };

  // Save tweet to pipeline function
  const saveTweetToPipeline = async (predictionId: number) => {
    if (!selectedSubmission || !customTweetText.trim()) return;

    setIsSavingToPipeline(true);
    try {
      const formData = new FormData();
      
      // Prepare tweet data
      const title = `Prediction: ${selectedSubmission.expert_name} - ${selectedSubmission.price_prediction}`;
      const topic = 'prediction';
      
      formData.append('title', title);
      formData.append('content', customTweetText);
      formData.append('topic', topic);
      formData.append('prediction_id', predictionId.toString());
      formData.append('influencer_id', (selectedInfluencerId || 0).toString());
      formData.append('created_by', 'admin');
      
      // Add images if selected
      selectedImages.forEach((image) => {
        formData.append('images', image);
      });

      const response = await api.post('/api/tweet-pipeline', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        console.log('✅ Tweet saved to pipeline successfully');
      } else {
        console.error('❌ Failed to save tweet to pipeline:', response.data.error);
      }
    } catch (error) {
      console.error('❌ Error saving tweet to pipeline:', error);
    } finally {
      setIsSavingToPipeline(false);
    }
  };

  // Tweet Pipeline Functions
  const fetchTweetPipelineItems = async () => {
    setIsLoadingTweets(true);
    try {
      const response = await api.get('/api/tweet-pipeline');
      console.log('API Response:', response.data); // Debug log
      if (response.data.success && response.data.tweets) {
        setTweetPipelineItems(response.data.tweets);
      } else {
        console.warn('Unexpected API response format:', response.data);
        setTweetPipelineItems([]);
      }
    } catch (error) {
      console.error('Error fetching tweet pipeline items:', error);
      setError('Failed to fetch tweets');
      setTweetPipelineItems([]); // Fallback to empty array
    } finally {
      setIsLoadingTweets(false);
    }
  };

  const handleCreateTweet = async () => {
    if (!tweetContent.trim()) return;

    try {
      const formData = new FormData();
      formData.append('content', tweetContent);
      
      if (tweetInfluencerId) {
        formData.append('influencer_id', tweetInfluencerId.toString());
      }
      
      if (tweetPredictionId) {
        formData.append('prediction_id', tweetPredictionId.toString());
      }
      
      if (tweetScheduledFor) {
        formData.append('scheduled_time', tweetScheduledFor);
      }

      // Add images
      tweetImages.forEach((image, index) => {
        formData.append('images', image);
      });

      const response = await api.post('/api/tweet-pipeline', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        await fetchTweetPipelineItems(); // Refresh list
        // Reset form
        setTweetContent('');
        setTweetInfluencerId(null);
        setTweetPredictionId(null);
        setTweetScheduledFor('');
        setTweetAIContext('');
        setTweetContextType('');
        setTweetImages([]);
        setTweetImagePreviews([]);
        setTweetPipelineModalMode('view');
      }
    } catch (error) {
      console.error('Error creating tweet:', error);
      setError('Failed to create tweet');
    }
  };

  const handleEditTweet = async () => {
    if (!selectedTweetPipeline || !tweetContent.trim()) return;

    try {
      const formData = new FormData();
      formData.append('content', tweetContent);
      
      if (tweetInfluencerId) {
        formData.append('influencer_id', tweetInfluencerId.toString());
      }
      
      if (tweetPredictionId) {
        formData.append('prediction_id', tweetPredictionId.toString());
      }
      
      if (tweetScheduledFor) {
        formData.append('scheduled_time', tweetScheduledFor);
      }

      // Add new images
      tweetImages.forEach((image, index) => {
        formData.append('images', image);
      });

      const response = await api.put(`/api/tweet-pipeline/${selectedTweetPipeline.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        await fetchTweetPipelineItems(); // Refresh list
        setTweetPipelineModalMode('view');
      }
    } catch (error) {
      console.error('Error updating tweet:', error);
      setError('Failed to update tweet');
    }
  };

  const handlePostTweet = async (tweetId: number) => {
    setIsPostingTweet(true);
    try {
      const response = await api.post(`/api/tweet-pipeline/${tweetId}/post`);
      
      if (response.data.success) {
        await fetchTweetPipelineItems(); // Refresh list
        console.log('✅ Tweet posted successfully');
      } else {
        console.error('❌ Failed to post tweet:', response.data.error);
        setError('Failed to post tweet');
      }
    } catch (error) {
      console.error('Error posting tweet:', error);
      setError('Failed to post tweet');
    } finally {
      setIsPostingTweet(false);
    }
  };

  const handleDeleteTweet = async (tweetId: number) => {
    if (!confirm('Are you sure you want to delete this tweet?')) return;

    try {
      const response = await api.delete(`/api/tweet-pipeline/${tweetId}`);
      
      if (response.data.success) {
        await fetchTweetPipelineItems(); // Refresh list
      }
    } catch (error) {
      console.error('Error deleting tweet:', error);
      setError('Failed to delete tweet');
    }
  };

  const handleRepostTweet = async (tweetId: number) => {
    if (!confirm('Are you sure you want to repost this tweet to X.com? This will create a duplicate post.')) return;

    setIsPostingTweet(true);
    try {
      const response = await api.post(`/api/tweet-pipeline/${tweetId}/repost`);
      
      if (response.data.success) {
        await fetchTweetPipelineItems(); // Refresh list
        console.log('✅ Tweet reposted successfully:', response.data.tweet_id);
        setMessage(`Tweet reposted successfully! Tweet ID: ${response.data.tweet_id}`);
      } else {
        console.error('❌ Failed to repost tweet:', response.data.error);
        setError('Failed to repost tweet');
      }
    } catch (error: any) {
      console.error('Error reposting tweet:', error);
      setError(error.response?.data?.error || 'Failed to repost tweet');
    } finally {
      setIsPostingTweet(false);
    }
  };

  const handleEditPipelineTweet = (tweet: TweetPipelineItem) => {
    setEditingTweet(tweet.id);
    setEditedTweetData({
      content: tweet.content,
      prediction_id: tweet.prediction_id,
      influencer_id: tweet.influencer_id
    });
  };

  const handleCancelEdit = () => {
    setEditingTweet(null);
    setEditedTweetData({});
    setTweetImages([]);
    setTweetImagePreviews([]);
  };

  const handleSaveEdit = async () => {
    if (!editingTweet) return;
    
    try {
      const formData = new FormData();
      formData.append('content', editedTweetData.content || '');
      
      if (editedTweetData.prediction_id) {
        formData.append('prediction_id', editedTweetData.prediction_id.toString());
      }
      
      if (editedTweetData.influencer_id) {
        formData.append('influencer_id', editedTweetData.influencer_id.toString());
      }
      
      // Add new screenshot if selected
      if (tweetImages.length > 0) {
        tweetImages.forEach(image => {
          formData.append('images', image);
        });
      }
      
      const response = await fetch(`/api/tweet-pipeline/${editingTweet}`, {
        method: 'PUT',
        body: formData
      });
      
      if (response.ok) {
        await fetchTweetPipelineItems();
        setMessage('Tweet updated successfully!');
        setEditingTweet(null);
        setEditedTweetData({});
        setTweetImages([]);
        setTweetImagePreviews([]);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update tweet');
      }
    } catch (error) {
      console.error('❌ Error updating tweet:', error);
      setError('Failed to update tweet');
    }
  };

  const handleRegenerateScreenshot = async (tweetId: number, predictionId: number) => {
    try {
      const response = await fetch(`/api/tweet-pipeline/${tweetId}/regenerate-screenshot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ prediction_id: predictionId })
      });
      
      if (response.ok) {
        await fetchTweetPipelineItems();
        setMessage('Screenshot regenerated successfully!');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to regenerate screenshot');
      }
    } catch (error) {
      console.error('❌ Error regenerating screenshot:', error);
      setError('Failed to regenerate screenshot');
    }
  };

  const handleChangeStatus = async (tweetId: number, newStatus: string) => {
    try {
      const formData = new FormData();
      formData.append('status', newStatus);

      const response = await api.put(`/api/tweet-pipeline/${tweetId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        await fetchTweetPipelineItems(); // Refresh list
      }
    } catch (error) {
      console.error('Error changing status:', error);
      setError('Failed to change status');
    }
  };

  const openTweetModal = (mode: 'create' | 'edit', tweet?: TweetPipelineItem) => {
    setTweetPipelineModalMode(mode);
    
    if (mode === 'edit' && tweet) {
      setSelectedTweetPipeline(tweet);
      setTweetContent(tweet.content);
      setTweetInfluencerId(tweet.influencer_id || null);
      setTweetPredictionId(tweet.prediction_id || null);
      setTweetScheduledFor(tweet.scheduled_time ? tweet.scheduled_time.slice(0, 16) : '');
      setTweetImages([]);
      setTweetImagePreviews([]);
    } else {
      setSelectedTweetPipeline(null);
      setTweetContent('');
      setTweetInfluencerId(null);
      setTweetPredictionId(null);
      setTweetScheduledFor('');
      setTweetAIContext('');
      setTweetContextType('');
      setTweetImages([]);
      setTweetImagePreviews([]);
    }
  };

  const handleTweetImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    // Add images directly without cropping
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    const newImages = [...tweetImages, ...imageFiles].slice(0, 4); // Max 4 images
    setTweetImages(newImages);
  };

  // Generate AI tweet content
  const generateAITweet = async () => {
    if (!tweetPredictionId) {
      alert('Please select a prediction first to generate AI content');
      return;
    }

    try {
      setIsGeneratingAI(true);
      
      const response = await api.post('/api/admin/generate-ai-tweet', {
        prediction_id: tweetPredictionId,
        post_type: 'new_prediction',
        options: {
          style: 'engaging',
          no_hashtags: true,
          context_type: tweetContextType,
          custom_context: tweetAIContext
        }
      });

      if (response.data.success) {
        setTweetContent(response.data.tweet);
        console.log('✅ AI tweet generated successfully');
      } else {
        console.error('❌ AI generation failed:', response.data.message);
        alert('AI generation failed: ' + response.data.message);
      }
    } catch (error) {
      console.error('❌ Error generating AI tweet:', error);
      alert('Error generating AI tweet. Please try again.');
    } finally {
      setIsGeneratingAI(false);
    }
  };

  // Generate prediction screenshot
  const generateScreenshot = async () => {
    if (!tweetPredictionId) {
      alert('Please select a prediction first to generate screenshot');
      return;
    }

    try {
      setIsGeneratingScreenshot(true);
      
      const response = await api.post('/api/admin/generate-screenshot', {
        predictionId: tweetPredictionId,
        influencerId: tweetInfluencerId,
        options: {
          style: 'professional'
        }
      });

      if (response.data.success) {
        // Convert the screenshot to a File object and add to images
        const screenshotUrl = `http://localhost:3001${response.data.url}`;
        
        // Fetch the screenshot as blob
        const imageResponse = await fetch(screenshotUrl);
        const blob = await imageResponse.blob();
        
        // Create file from blob
        const file = new File([blob], response.data.filename, { type: 'image/png' });
        
        // Add to tweet images
        const newImages = [...tweetImages, file].slice(0, 4);
        setTweetImages(newImages);
        
        // Add preview
        const reader = new FileReader();
        reader.onload = (e) => {
          const newPreviews = [...tweetImagePreviews, e.target?.result as string];
          setTweetImagePreviews(newPreviews);
        };
        reader.readAsDataURL(file);
        
        console.log('✅ Chart screenshot generated and added to tweet');
      } else {
        console.error('❌ Screenshot generation failed:', response.data.message);
        alert('Screenshot generation failed: ' + response.data.message);
      }
    } catch (error) {
      console.error('❌ Error generating screenshot:', error);
      alert('Error generating screenshot. Please try again.');
    } finally {
      setIsGeneratingScreenshot(false);
    }
  };

  // Load tweet pipeline items when tab is active
  useEffect(() => {
    if (activeTab === 'tweet-pipeline') {
      fetchTweetPipelineItems();
    }
  }, [activeTab]);

  // Set up paste event listeners
  useEffect(() => {
    const socialArea = socialPasteAreaRef.current;
    const tweetArea = tweetPasteAreaRef.current;
    
    if (socialArea) {
      socialArea.addEventListener('paste', handleSocialPaste as any);
      socialArea.setAttribute('tabindex', '0'); // Make it focusable
    }
    
    if (tweetArea) {
      tweetArea.addEventListener('paste', handleTweetPaste as any);
      tweetArea.setAttribute('tabindex', '0'); // Make it focusable
    }
    
    return () => {
      if (socialArea) {
        socialArea.removeEventListener('paste', handleSocialPaste as any);
      }
      if (tweetArea) {
        tweetArea.removeEventListener('paste', handleTweetPaste as any);
      }
    };
  }, [selectedSubmission, tweetPipelineModalMode]);
  
  const handleReject = async () => {
    if (!selectedSubmission) return;
    
    try {
      
      await api.put(`/api/admin/prediction_submissions/${selectedSubmission.id}`, {
        status: 'rejected',
        admin_comment: comment
      });
      
      // Refresh submissions
      const response = await api.get(`/api/admin/prediction_submissions?status=${activeTab}`);
      setSubmissions(response.data);
      
      alert('Submission rejected successfully!');
      
      closeModal();
    } catch (err) {
      console.error('Error rejecting submission:', err);
      // Show error
    }
  };
  
  const handleSavePredictionDate = async () => {
    if (!selectedSubmission) return;
    
    try {
      // Use the global apiBase that handles dev vs prod properly
      
      const predictionMadeDate = editedData.prediction_made || selectedSubmission?.prediction_made || new Date().toISOString().split('T')[0];
      
      await api.put(`/api/admin/prediction_submissions/${selectedSubmission.id}/prediction-date`, {
        prediction_made: predictionMadeDate
      });
      
      // Update the selected submission to reflect the change
      setSelectedSubmission({
        ...selectedSubmission,
        prediction_made: predictionMadeDate
      });
      
      // Refresh submissions to show updated data
      const response = await api.get(`/api/admin/prediction_submissions?status=${activeTab}`);
      setSubmissions(response.data);
      
      alert('Prediction date saved successfully!');
    } catch (err) {
      console.error('Error saving prediction date:', err);
      alert('Error saving prediction date. Please try again.');
    }
  };
  
  // Update the formatPrediction function to better handle the data
  const formatPrediction = (submission: Submission): string => {
    
    try {
      // For 'target' type predictions with price_target value
      if (submission.prediction_type === 'target' && submission.price_target) {
        return `$${Number(submission.price_target).toLocaleString()}`;
      }
      
      // For 'range' type predictions with min and max values
      if (submission.prediction_type === 'range' && submission.price_min && submission.price_max) {
        return `$${Number(submission.price_min).toLocaleString()} - $${Number(submission.price_max).toLocaleString()}`;
      }
      
      // For 'pricetimerange' type predictions with min value
      if (submission.prediction_type === 'pricetimerange' && submission.price_min) {
        return `Above $${Number(submission.price_min).toLocaleString()}`;
      }
      
      // If prediction_type doesn't match but we have price data, use it
      if (submission.price_target) {
        return `$${Number(submission.price_target).toLocaleString()}`;
      }
      
      if (submission.price_min && submission.price_max) {
        return `$${Number(submission.price_min).toLocaleString()} - $${Number(submission.price_max).toLocaleString()}`;
      }
      
      if (submission.price_min) {
        return `Above $${Number(submission.price_min).toLocaleString()}`;
      }
      
      if (submission.price_max) {
        return `Below $${Number(submission.price_max).toLocaleString()}`;
      }
      
      // Legacy field
      if ((submission as any).price_prediction) {
        return (submission as any).price_prediction;
      }
      
      // Last resort
      return submission.prediction_type ? 
        `${submission.prediction_type} prediction` : 
        'Unknown prediction';
    } catch (error) {
      console.error('Error formatting prediction:', error);
      return 'Error formatting prediction';
    }
  };
  
  // Format date consistently in European format (DD/MM/YYYY)
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleDateString('en-GB'); // European format: DD/MM/YYYY
    } catch (e) {
      return 'Invalid Date';
    }
  };
  
  return (
    <Container>
      <Header>
        <Title>Admin Dashboard - Prediction Submissions</Title>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button 
            onClick={() => navigate('/admin/mstr')}
            style={{
              padding: '8px 16px',
              background: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            MSTR Dashboard
          </button>
          <LogoutButton onClick={handleLogout}>
            Logout
          </LogoutButton>
        </div>
      </Header>
      
      <Tabs>
        <Tab 
          active={activeTab === 'pending'} 
          onClick={() => setActiveTab('pending')}
        >
          Pending
        </Tab>
        <Tab 
          active={activeTab === 'approved'} 
          onClick={() => setActiveTab('approved')}
        >
          Approved
        </Tab>
        <Tab 
          active={activeTab === 'rejected'} 
          onClick={() => setActiveTab('rejected')}
        >
          Rejected
        </Tab>
        <Tab 
          active={activeTab === 'all'} 
          onClick={() => setActiveTab('all')}
        >
          All
        </Tab>
        <Tab 
          active={activeTab === 'link-submissions'} 
          onClick={() => setActiveTab('link-submissions')}
        >
          Link Submissions
        </Tab>
        <Tab 
          active={activeTab === 'x-com'} 
          onClick={() => setActiveTab('x-com')}
        >
          X.com Posts
        </Tab>
        <Tab 
          active={activeTab === 'tweet-pipeline'} 
          onClick={() => setActiveTab('tweet-pipeline')}
        >
          🚀 Tweet Pipeline
        </Tab>
        <Tab 
          active={activeTab === 'tweet-extraction'} 
          onClick={() => setActiveTab('tweet-extraction')}
        >
          Tweet Extraction
        </Tab>
        <Tab 
          active={activeTab === 'prediction-manager'} 
          onClick={() => setActiveTab('prediction-manager')}
        >
          🎯 Prediction Manager
        </Tab>
        <Tab 
          active={activeTab === 'bulk-tweets'} 
          onClick={() => setActiveTab('bulk-tweets')}
        >
          📥 Bulk Tweets
        </Tab>
        <Tab 
          active={activeTab === 'curated-tweets'} 
          onClick={() => {
            setActiveTab('curated-tweets');
            // Reset to first step when switching to curated tab
            if (activeTab !== 'curated-tweets') {
              setStep('select');
              setRealTweets([]);
              setSelectedTweetIds(new Set());
            }
          }}
        >
          📊 Curated Tweets ({step === 'select' ? 'Step 1' : step === 'review' ? 'Step 2' : 'Step 3'})
        </Tab>
      </Tabs>
      
      {activeTab === 'x-com' ? (
        <EnhancedXComControls />
      ) : activeTab === 'tweet-pipeline' ? (
        <XComSection>
          <SectionTitle>🚀 Tweet Pipeline</SectionTitle>
          
          <QuickActions>
            <ActionButton onClick={() => openTweetModal('create')}>
              ✏️ Create New Tweet
            </ActionButton>
          </QuickActions>

          {isLoadingTweets ? (
            <div style={{ textAlign: 'center', padding: '20px', color: theme.subText }}>
              Loading tweets...
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '15px', marginTop: '20px' }}>
              {tweetPipelineItems.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '40px', 
                  color: theme.subText,
                  background: theme.surface,
                  borderRadius: '8px'
                }}>
                  No tweets in pipeline. Create your first tweet!
                </div>
              ) : (
                tweetPipelineItems.map((tweet) => (
                  <div
                    key={tweet.id}
                    style={{
                      background: theme.surface,
                      border: `1px solid ${theme.border}`,
                      borderRadius: '8px',
                      padding: '20px'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
                      <div>
                        <div style={{
                          display: 'inline-block',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          marginBottom: '10px',
                          background: tweet.status === 'posted' ? '#4CAF5020' : 
                                    tweet.status === 'ready' ? '#2196F320' :
                                    tweet.status === 'scheduled' ? '#ff980020' :
                                    tweet.status === 'failed' ? '#f4433620' : '#9e9e9e20',
                          color: tweet.status === 'posted' ? '#4CAF50' : 
                                tweet.status === 'ready' ? '#2196F3' :
                                tweet.status === 'scheduled' ? '#ff9800' :
                                tweet.status === 'failed' ? '#f44336' : '#9e9e9e'
                        }}>
                          {tweet.status.toUpperCase()}
                        </div>
                        {tweet.influencer_name && (
                          <div style={{ fontSize: '14px', color: theme.subText, marginBottom: '8px' }}>
                            👤 {tweet.influencer_name}
                          </div>
                        )}
                        {tweet.scheduled_time && (
                          <div style={{ fontSize: '14px', color: theme.subText, marginBottom: '8px' }}>
                            ⏰ Scheduled for: {new Date(tweet.scheduled_time).toLocaleString()}
                          </div>
                        )}
                      </div>
                      
                      <QuickActions>
                        {tweet.status !== 'posted' && (
                          <>
                            <ActionButton 
                              onClick={() => openTweetModal('edit', tweet)}
                              style={{ padding: '8px 12px', fontSize: '12px' }}
                            >
                              ✏️ Edit
                            </ActionButton>
                            {tweet.status === 'draft' && (
                              <ActionButton 
                                onClick={() => handleChangeStatus(tweet.id, 'ready')}
                                style={{ 
                                  padding: '8px 12px', 
                                  fontSize: '12px',
                                  background: '#4CAF50'
                                }}
                              >
                                ✅ Mark Ready
                              </ActionButton>
                            )}
                            {(tweet.status === 'ready' || tweet.status === 'scheduled') && (
                              <ActionButton 
                                onClick={() => handlePostTweet(tweet.id)}
                                disabled={isPostingTweet}
                                style={{ 
                                  padding: '8px 12px', 
                                  fontSize: '12px',
                                  background: '#1DA1F2'
                                }}
                              >
                                {isPostingTweet ? '⏳' : '📤 Post'}
                              </ActionButton>
                            )}
                          </>
                        )}
                        {tweet.status === 'posted' && (
                          <>
                            <ActionButton 
                              onClick={() => handleRepostTweet(tweet.id)}
                              disabled={isPostingTweet}
                              style={{ 
                                padding: '8px 12px', 
                                fontSize: '12px',
                                background: '#17a2b8'
                              }}
                            >
                              {isPostingTweet ? '⏳' : '🔄 Repost'}
                            </ActionButton>
                            <ActionButton 
                              onClick={() => handleEditPipelineTweet(tweet)}
                              style={{ 
                                padding: '8px 12px', 
                                fontSize: '12px',
                                background: '#ffc107',
                                color: '#000'
                              }}
                            >
                              ✏️ Edit
                            </ActionButton>
                          </>
                        )}
                        <ActionButton 
                          onClick={() => handleDeleteTweet(tweet.id)}
                          style={{ 
                            padding: '8px 12px', 
                            fontSize: '12px',
                            background: '#f44336'
                          }}
                        >
                          🗑️ Delete
                        </ActionButton>
                      </QuickActions>
                    </div>

                    <div style={{ color: theme.text, marginBottom: '15px', lineHeight: '1.5' }}>
                      {tweet.content}
                    </div>

                    {tweet.custom_images && tweet.custom_images.length > 0 && (
                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '15px' }}>
                        {tweet.custom_images.map((imagePath, index) => (
                          <img
                            key={index}
                            src={`${apiBase}${imagePath}`}
                            alt={`Tweet image ${index + 1}`}
                            style={{
                              width: '100px',
                              height: '100px',
                              objectFit: 'cover',
                              borderRadius: '8px',
                              border: `1px solid ${theme.border}`
                            }}
                          />
                        ))}
                      </div>
                    )}

                    <div style={{ fontSize: '12px', color: theme.subText }}>
                      Created: {new Date(tweet.created_at).toLocaleString()}
                      {tweet.posted_at && (
                        <> • Posted: {new Date(tweet.posted_at).toLocaleString()}</>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Edit Tweet Modal */}
          {editingTweet && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.7)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000
            }}>
              <div style={{
                backgroundColor: theme.surface,
                padding: '30px',
                borderRadius: '8px',
                width: '90%',
                maxWidth: '600px',
                maxHeight: '80vh',
                overflow: 'auto',
                border: `1px solid ${theme.border}`
              }}>
                <h3 style={{ color: theme.text, marginBottom: '20px' }}>Edit Tweet</h3>
                
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ color: theme.text, display: 'block', marginBottom: '5px' }}>Content:</label>
                  <textarea
                    value={editedTweetData.content || ''}
                    onChange={(e) => setEditedTweetData({...editedTweetData, content: e.target.value})}
                    style={{
                      width: '100%',
                      height: '120px',
                      padding: '10px',
                      borderRadius: '4px',
                      border: `1px solid ${theme.border}`,
                      backgroundColor: theme.background,
                      color: theme.text,
                      resize: 'vertical'
                    }}
                  />
                </div>
                
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ color: theme.text, display: 'block', marginBottom: '5px' }}>Prediction:</label>
                  <select
                    value={editedTweetData.prediction_id || ''}
                    onChange={(e) => setEditedTweetData({...editedTweetData, prediction_id: parseInt(e.target.value)})}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '4px',
                      border: `1px solid ${theme.border}`,
                      backgroundColor: theme.background,
                      color: theme.text
                    }}
                  >
                    <option value="">Select a prediction...</option>
                    {predictions.map(prediction => (
                      <option key={prediction.id} value={prediction.id}>
                        {prediction.expert_name} - ${prediction.price_prediction}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ color: theme.text, display: 'block', marginBottom: '5px' }}>New Screenshot (optional):</label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      setTweetImages(files);
                      
                      // Generate previews
                      const previews = files.map(file => URL.createObjectURL(file));
                      setTweetImagePreviews(previews);
                    }}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '4px',
                      border: `1px solid ${theme.border}`,
                      backgroundColor: theme.background,
                      color: theme.text
                    }}
                  />
                  
                  {tweetImagePreviews.length > 0 && (
                    <div style={{ marginTop: '10px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      {tweetImagePreviews.map((preview, index) => (
                        <img
                          key={index}
                          src={preview}
                          alt={`Preview ${index}`}
                          style={{
                            width: '100px',
                            height: '100px',
                            objectFit: 'cover',
                            borderRadius: '4px'
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
                
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <ActionButton onClick={handleCancelEdit} style={{ background: '#6c757d' }}>
                    Cancel
                  </ActionButton>
                  <ActionButton onClick={handleSaveEdit} style={{ background: '#28a745' }}>
                    Save Changes
                  </ActionButton>
                  {editedTweetData.prediction_id && (
                    <ActionButton 
                      onClick={() => handleRegenerateScreenshot(editingTweet!, editedTweetData.prediction_id!)}
                      style={{ background: '#007bff' }}
                    >
                      🔄 Regenerate Screenshot
                    </ActionButton>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Tweet Creation/Edit Modal */}
          {tweetPipelineModalMode !== 'view' && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}>
              <div style={{
                background: theme.surface,
                border: `1px solid ${theme.border}`,
                borderRadius: '12px',
                padding: '30px',
                maxWidth: '600px',
                width: '90%',
                maxHeight: '80vh',
                overflow: 'auto'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3 style={{ color: theme.text, margin: 0 }}>
                    {tweetPipelineModalMode === 'create' ? 'Create New Tweet' : 'Edit Tweet'}
                  </h3>
                  <button 
                    onClick={() => setTweetPipelineModalMode('view')}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '20px',
                      color: theme.text,
                      cursor: 'pointer'
                    }}
                  >
                    ✕
                  </button>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', color: theme.text, fontWeight: 'bold' }}>
                    Tweet Content
                  </label>
                  <textarea
                    value={tweetContent}
                    onChange={(e) => setTweetContent(e.target.value)}
                    placeholder="What's happening?"
                    style={{
                      width: '100%',
                      height: '120px',
                      padding: '12px',
                      border: `1px solid ${theme.border}`,
                      borderRadius: '8px',
                      background: theme.background,
                      color: theme.text,
                      fontSize: '16px',
                      resize: 'vertical'
                    }}
                  />
                  <div style={{ fontSize: '12px', color: theme.subText, marginTop: '5px' }}>
                    {tweetContent.length}/280 characters
                  </div>
                  
                  <div style={{ marginTop: '10px' }}>
                    <button
                      onClick={generateAITweet}
                      disabled={isGeneratingAI || !tweetPredictionId}
                      style={{
                        padding: '8px 16px',
                        background: isGeneratingAI ? '#666' : 'linear-gradient(45deg, #ff6b35, #ff8e53)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: isGeneratingAI ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        opacity: !tweetPredictionId ? 0.6 : 1
                      }}
                      title={!tweetPredictionId ? 'Select a prediction first' : 'Generate AI-powered tweet using Grok'}
                    >
                      {isGeneratingAI ? '🤖 Generating...' : '🤖 Generate AI Tweet'}
                    </button>
                    {!tweetPredictionId && (
                      <div style={{ fontSize: '12px', color: theme.subText, marginTop: '5px' }}>
                        Select a prediction to enable AI generation
                      </div>
                    )}
                    
                    <button
                      onClick={generateScreenshot}
                      disabled={isGeneratingScreenshot || !tweetPredictionId}
                      style={{
                        padding: '8px 16px',
                        background: isGeneratingScreenshot ? '#666' : 'linear-gradient(45deg, #3b82f6, #1d4ed8)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: isGeneratingScreenshot ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        opacity: !tweetPredictionId ? 0.6 : 1,
                        marginLeft: '10px'
                      }}
                      title={!tweetPredictionId ? 'Select a prediction first' : 'Generate live chart screenshot with prediction overlay'}
                    >
                      {isGeneratingScreenshot ? '� Capturing Chart...' : '� Chart Screenshot'}
                    </button>
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', color: theme.text, fontWeight: 'bold' }}>
                    Influencer (optional)
                  </label>
                  <select
                    value={tweetInfluencerId || ''}
                    onChange={(e) => setTweetInfluencerId(e.target.value ? parseInt(e.target.value) : null)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: `1px solid ${theme.border}`,
                      borderRadius: '8px',
                      background: theme.background,
                      color: theme.text,
                      fontSize: '16px'
                    }}
                  >
                    <option value="">Select an influencer...</option>
                    {influencers.map((influencer) => (
                      <option key={influencer.id} value={influencer.id}>
                        {influencer.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', color: theme.text, fontWeight: 'bold' }}>
                    Link to Prediction (optional)
                  </label>
                  <select
                    value={tweetPredictionId || ''}
                    onChange={(e) => setTweetPredictionId(e.target.value ? parseInt(e.target.value) : null)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: `1px solid ${theme.border}`,
                      borderRadius: '8px',
                      background: theme.background,
                      color: theme.text,
                      fontSize: '16px'
                    }}
                  >
                    <option value="">Select a prediction...</option>
                    {predictions.map((prediction) => (
                      <option key={prediction.id} value={prediction.id}>
                        {`${prediction.expert_name} - $${prediction.price_prediction} (${prediction.submission_date ? new Date(prediction.submission_date).toLocaleDateString() : 'No date'})`}
                      </option>
                    ))}
                  </select>
                </div>

                {/* AI Context Controls */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', color: theme.text, fontWeight: 'bold' }}>
                    AI Context (optional)
                  </label>
                  <select
                    value={tweetContextType}
                    onChange={(e) => setTweetContextType(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: `1px solid ${theme.border}`,
                      borderRadius: '8px',
                      background: theme.background,
                      color: theme.text,
                      fontSize: '16px',
                      marginBottom: '12px'
                    }}
                  >
                    <option value="">Choose context type...</option>
                    <option value="new_prediction_added">New Prediction Added</option>
                    <option value="status_update_pending_to_miss">Status Update: Pending → Miss</option>
                    <option value="status_update_pending_to_success">Status Update: Pending → Success</option>
                    <option value="bottom_breached">Bottom Prediction Breached</option>
                    <option value="target_missed">Target Prediction Missed</option>
                    <option value="target_hit">Target Prediction Hit</option>
                    <option value="prediction_delayed">Prediction Delayed</option>
                    <option value="prediction_updated">Prediction Updated</option>
                    <option value="price_proximity">Price Near Target/Bottom</option>
                    <option value="bullish_momentum">Bullish Momentum Analysis</option>
                    <option value="bearish_warning">Bearish Warning Analysis</option>
                  </select>
                  
                  <textarea
                    placeholder="Add custom context for AI (e.g., 'This was a $100k bottom prediction and current price $89k means the bottom was breached')"
                    value={tweetAIContext}
                    onChange={(e) => setTweetAIContext(e.target.value)}
                    style={{
                      width: '100%',
                      minHeight: '80px',
                      padding: '12px',
                      border: `1px solid ${theme.border}`,
                      borderRadius: '8px',
                      background: theme.background,
                      color: theme.text,
                      fontSize: '14px',
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                  />
                  <div style={{ fontSize: '12px', color: theme.subText, marginTop: '5px' }}>
                    Provide specific context to help AI understand the prediction scenario better
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', color: theme.text, fontWeight: 'bold' }}>
                    Schedule for later (optional)
                  </label>
                  <input
                    type="datetime-local"
                    value={tweetScheduledFor}
                    onChange={(e) => setTweetScheduledFor(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: `1px solid ${theme.border}`,
                      borderRadius: '8px',
                      background: theme.background,
                      color: theme.text,
                      fontSize: '16px'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', color: theme.text, fontWeight: 'bold' }}>
                    Images (up to 4)
                  </label>
                  
                  {/* Enhanced Paste/Drop Area for Tweet Pipeline */}
                  <PasteDropArea 
                    ref={tweetPasteAreaRef}
                    isDragOver={isDragOverTweet}
                    isPasting={isPastingTweet}
                    onClick={() => document.getElementById('tweet-file-input')?.click()}
                    onDragOver={handleTweetDragOver}
                    onDragLeave={handleTweetDragLeave}
                    onDrop={handleTweetDrop}
                    style={{
                      background: isPastingTweet ? 'rgba(76, 175, 80, 0.1)' :
                                 isDragOverTweet ? 'rgba(33, 150, 243, 0.1)' : 
                                 theme.surface,
                      border: `2px dashed ${isPastingTweet ? '#4CAF50' :
                              isDragOverTweet ? '#2196F3' : 
                              theme.border}`,
                      color: theme.text
                    }}
                  >
                    <PasteInstructions style={{ color: theme.subText }}>
                      📋 <strong>Paste screenshots:</strong> <span className="shortcut">Ctrl+V</span> / <span className="shortcut">Cmd+V</span><br/>
                      📁 <strong>Drag & drop</strong> image files here<br/>
                      📤 <strong>Click to browse</strong> files (max 4 images)
                    </PasteInstructions>
                    
                    <div style={{ fontSize: '12px', color: theme.subText }}>
                      Upload images as-is • No file saving required!
                    </div>
                    
                    <PasteIndicator show={isPastingTweet}>
                      ✅ Image pasted from clipboard!
                    </PasteIndicator>
                  </PasteDropArea>
                  
                  <input
                    id="tweet-file-input"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleTweetImageSelect}
                    style={{ display: 'none' }}
                  />
                  
                  {tweetImagePreviews.length > 0 && (
                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
                      {tweetImagePreviews.map((preview, index) => (
                        <img
                          key={index}
                          src={preview}
                          alt={`Preview ${index + 1}`}
                          style={{
                            width: '80px',
                            height: '80px',
                            objectFit: 'cover',
                            borderRadius: '8px',
                            border: `1px solid ${theme.border}`
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '20px', padding: '15px', background: `${theme.surface}50`, borderRadius: '8px', border: `1px solid ${theme.border}` }}>
                  <label style={{ display: 'flex', alignItems: 'center', color: theme.text, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      style={{ marginRight: '8px' }}
                      title="Enable cross-posting to Nostr protocol alongside Twitter/X"
                    />
                    <span>📡 Also post to Nostr (decentralized social)</span>
                  </label>
                  <div style={{ fontSize: '12px', color: theme.subText, marginTop: '5px' }}>
                    Cross-post to decentralized social networks
                  </div>
                </div>

                <QuickActions>
                  <ActionButton 
                    onClick={() => setTweetPipelineModalMode('view')}
                    style={{ background: theme.surface, color: theme.text, border: `1px solid ${theme.border}` }}
                  >
                    Cancel
                  </ActionButton>
                  <ActionButton 
                    onClick={tweetPipelineModalMode === 'create' ? handleCreateTweet : handleEditTweet}
                    disabled={!tweetContent.trim()}
                  >
                    {tweetPipelineModalMode === 'create' ? 'Create Tweet' : 'Save Changes'}
                  </ActionButton>
                </QuickActions>
              </div>
            </div>
          )}
        </XComSection>
      ) : activeTab === 'tweet-extraction' ? (
        <>
          <TweetSubmissionForm />
          <TweetQueueManager />
        </>
      ) : activeTab === 'prediction-manager' ? (
        <PredictionManager theme={theme} />
      ) : activeTab === 'bulk-tweets' ? (
        <div style={{ padding: '20px', backgroundColor: '#2d3748', borderRadius: '8px', color: '#e2e8f0' }}>
          <h2 style={{ color: '#3182ce', marginBottom: '20px' }}>📥 Bulk Tweet Archive System</h2>
          
          {/* Download Section */}
          <div style={{ marginBottom: '30px', padding: '20px', backgroundColor: '#374151', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: '15px' }}>Start Bulk Download</h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px' }}>Select Influencer:</label>
                <select 
                  value={selectedInfluencerForBulk || ''} 
                  onChange={(e) => setSelectedInfluencerForBulk(Number(e.target.value) || null)}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #4a5568', backgroundColor: '#1a202c', color: '#e2e8f0' }}
                >
                  <option value="">Select Influencer</option>
                  {influencers.map(influencer => (
                    <option key={influencer.id} value={influencer.id}>
                      {influencer.name} (@{influencer.x_handle})
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '5px' }}>Start Date:</label>
                <input 
                  type="date" 
                  value={bulkStartDate} 
                  onChange={(e) => setBulkStartDate(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #4a5568', backgroundColor: '#1a202c', color: '#e2e8f0' }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '5px' }}>End Date:</label>
                <input 
                  type="date" 
                  value={bulkEndDate} 
                  onChange={(e) => setBulkEndDate(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #4a5568', backgroundColor: '#1a202c', color: '#e2e8f0' }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '5px' }}>Max Tweets:</label>
                <input 
                  type="number" 
                  value={maxTweets} 
                  onChange={(e) => setMaxTweets(Number(e.target.value))}
                  min="100" 
                  max="5000"
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #4a5568', backgroundColor: '#1a202c', color: '#e2e8f0' }}
                />
              </div>
            </div>
            
            <button 
              onClick={startBulkDownload}
              disabled={!selectedInfluencerForBulk || isDownloadingBulk}
              style={{
                padding: '10px 20px',
                backgroundColor: selectedInfluencerForBulk && !isDownloadingBulk ? '#f7931a' : '#4a5568',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: selectedInfluencerForBulk && !isDownloadingBulk ? 'pointer' : 'not-allowed',
                fontWeight: 'bold'
              }}
            >
              {isDownloadingBulk ? '🔄 Starting Download...' : '🚀 Start Bulk Download'}
            </button>
            
            {bulkDownloadStatus && (
              <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#1a202c', borderRadius: '4px', border: '1px solid #4a5568' }}>
                <strong>Status:</strong> {bulkDownloadStatus}
              </div>
            )}
          </div>
          
          {/* Quick Actions */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '20px' }}>
            <button 
              onClick={() => selectedInfluencerForBulk && getInfluencerBulkStats(selectedInfluencerForBulk)}
              disabled={!selectedInfluencerForBulk}
              style={{
                padding: '15px',
                backgroundColor: '#374151',
                border: '1px solid #4a5568',
                borderRadius: '8px',
                color: '#e2e8f0',
                cursor: selectedInfluencerForBulk ? 'pointer' : 'not-allowed'
              }}
            >
              📊 Load Influencer Stats
            </button>
            
            <button 
              onClick={() => searchBulkTweets('bitcoin')}
              style={{
                padding: '15px',
                backgroundColor: '#374151',
                border: '1px solid #4a5568',
                borderRadius: '8px',
                color: '#e2e8f0',
                cursor: 'pointer'
              }}
            >
              🔍 Search Bitcoin Tweets
            </button>
          </div>
          
          {/* Analytics Display */}
          {bulkAnalytics && (
            <div style={{ marginBottom: '20px', padding: '20px', backgroundColor: '#374151', borderRadius: '8px' }}>
              <h3 style={{ marginBottom: '15px' }}>📈 Influencer Analytics</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px' }}>
                <div style={{ textAlign: 'center', padding: '10px', backgroundColor: '#1a202c', borderRadius: '4px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f7931a' }}>{bulkAnalytics.total_archived_tweets || 0}</div>
                  <div style={{ fontSize: '12px', color: '#a0aec0' }}>TOTAL TWEETS</div>
                </div>
                <div style={{ textAlign: 'center', padding: '10px', backgroundColor: '#1a202c', borderRadius: '4px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f7931a' }}>{bulkAnalytics.bitcoin_prediction_tweets || 0}</div>
                  <div style={{ fontSize: '12px', color: '#a0aec0' }}>BITCOIN PREDICTIONS</div>
                </div>
                <div style={{ textAlign: 'center', padding: '10px', backgroundColor: '#1a202c', borderRadius: '4px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f7931a' }}>{bulkAnalytics.avg_prediction_confidence || 0}</div>
                  <div style={{ fontSize: '12px', color: '#a0aec0' }}>AVG CONFIDENCE</div>
                </div>
                <div style={{ textAlign: 'center', padding: '10px', backgroundColor: '#1a202c', borderRadius: '4px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f7931a' }}>{bulkAnalytics.promoted_to_predictions || 0}</div>
                  <div style={{ fontSize: '12px', color: '#a0aec0' }}>PROMOTED</div>
                </div>
              </div>
            </div>
          )}
          
          {/* Tweet Results */}
          {bulkTweets.length > 0 && (
            <div style={{ padding: '20px', backgroundColor: '#374151', borderRadius: '8px' }}>
              <h3 style={{ marginBottom: '15px' }}>🐦 Tweet Results ({bulkTweets.length})</h3>
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {bulkTweets.slice(0, 10).map((tweet, index) => (
                  <div key={index} style={{ padding: '15px', marginBottom: '10px', backgroundColor: '#1a202c', borderRadius: '4px', borderLeft: '3px solid #f7931a' }}>
                    <div style={{ fontSize: '14px', marginBottom: '8px' }}>
                      <strong>@{tweet.author_username}</strong> • {new Date(tweet.tweet_created_at).toLocaleDateString()}
                    </div>
                    <div style={{ marginBottom: '8px', fontSize: '14px' }}>{tweet.tweet_text}</div>
                    {tweet.predicted_price && (
                      <div style={{ fontSize: '12px', color: '#a0aec0' }}>
                        💰 Prediction: ${tweet.predicted_price} • Confidence: {(tweet.confidence_score * 100).toFixed(1)}%
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#374151', borderRadius: '8px', fontSize: '14px' }}>
            <strong>Instructions:</strong>
            <ul style={{ marginLeft: '20px', marginTop: '10px', color: '#a0aec0' }}>
              <li>Select Peter Schiff from the dropdown to test the bulk download system</li>
              <li>Set a date range (recommend last 6 months for testing)</li>
              <li>Start with 500-1000 max tweets to avoid overwhelming the system</li>
              <li>The system will download, analyze with AI, and store tweets in the archive</li>
              <li>Use search and analytics to explore the downloaded data</li>
            </ul>
          </div>
        </div>
      ) : activeTab === 'curated-tweets' ? (
        <div style={{ padding: '20px', backgroundColor: '#2d3748', borderRadius: '8px', color: '#e2e8f0' }}>
          <h2 style={{ color: '#3182ce', marginBottom: '20px' }}>📊 Curated Tweets via Grok AI</h2>
          
          {/* Curation Section */}
          <div style={{ marginBottom: '30px', padding: '20px', backgroundColor: '#374151', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: '15px' }}>
              {step === 'select' ? '📥 Step 1: Fetch Real Tweets' : 
               step === 'review' ? '🔍 Step 2: Review & Select Tweets' :
               '🤖 Step 3: Analyze with Grok AI'}
            </h3>
            
            {step === 'select' && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '20px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px' }}>Select Influencer:</label>
                    <select 
                      value={selectedInfluencer?.toString() || ''} 
                      onChange={(e) => setSelectedInfluencer(Number(e.target.value) || null)}
                      style={{ width: '100%', padding: '12px', borderRadius: '4px', border: '1px solid #4a5568', backgroundColor: '#1a202c', color: '#e2e8f0', fontSize: '14px' }}
                    >
                      <option value="">Select Influencer</option>
                      {influencers.map(influencer => (
                        <option key={influencer.id} value={influencer.id}>
                          {influencer.name} (@{influencer.x_handle})
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px' }}>Max Tweets to Fetch:</label>
                    <select 
                      value={curationCount * 3} 
                      onChange={(e) => setCurationCount(Math.round(Number(e.target.value) / 3))}
                      style={{ width: '100%', padding: '12px', borderRadius: '4px', border: '1px solid #4a5568', backgroundColor: '#1a202c', color: '#e2e8f0', fontSize: '14px' }}
                    >
                      <option value={30}>30 tweets</option>
                      <option value={60}>60 tweets</option>
                      <option value={90}>90 tweets</option>
                      <option value={120}>120 tweets</option>
                      <option value={150}>150 tweets</option>
                      <option value={180}>180 tweets</option>
                      <option value={210}>210 tweets</option>
                      <option value={240}>240 tweets</option>
                      <option value={270}>270 tweets</option>
                      <option value={300}>300 tweets</option>
                    </select>
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px' }}>Time Period (months, max 120 for full history):</label>
                    <input 
                      type="number" 
                      value={curationMonths} 
                      onChange={(e) => setCurationMonths(Number(e.target.value))}
                      min="1" 
                      max="120"
                      style={{ width: '100%', padding: '12px', borderRadius: '4px', border: '1px solid #4a5568', backgroundColor: '#1a202c', color: '#e2e8f0', fontSize: '14px' }}
                    />
                  </div>
                </div>
                
                <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#1a202c', borderRadius: '4px', fontSize: '14px' }}>
                  💰 <strong>Cost Estimate:</strong> ${((curationCount * 3) * 0.0001).toFixed(4)} for X API tweet fetching
                </div>
                
                <button 
                  onClick={fetchRealTweets}
                  disabled={!selectedInfluencer || isLoadingRealTweets}
                  style={{
                    padding: '15px 30px',
                    backgroundColor: selectedInfluencer && !isLoadingRealTweets ? '#3182ce' : '#4a5568',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: selectedInfluencer && !isLoadingRealTweets ? 'pointer' : 'not-allowed',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    minHeight: '50px'
                  }}
                >
                  {isLoadingRealTweets ? '📥 Fetching Real Tweets...' : '📥 Fetch Real Tweets'}
                </button>
              </>
            )}

            {step === 'review' && (
              <>
                <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <strong>{realTweets.length}</strong> real tweets fetched • 
                    <strong> {selectedTweetIds.size}</strong> selected
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button 
                      onClick={() => setStep('select')}
                      style={{
                        padding: '12px 20px',
                        backgroundColor: '#6b7280',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}
                    >
                      ← Back
                    </button>
                    <button 
                      onClick={analyzeSelectedTweets}
                      disabled={selectedTweetIds.size === 0 || isCurating}
                      style={{
                        padding: '12px 20px',
                        backgroundColor: selectedTweetIds.size > 0 && !isCurating ? '#f7931a' : '#4a5568',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: selectedTweetIds.size > 0 && !isCurating ? 'pointer' : 'not-allowed',
                        fontWeight: 'bold',
                        fontSize: '14px'
                      }}
                    >
                      {isCurating ? '🤖 Analyzing...' : `🤖 Analyze ${selectedTweetIds.size} Tweets ($${(selectedTweetIds.size * 0.01).toFixed(2)})`}
                    </button>
                  </div>
                </div>
                
                <div style={{ maxHeight: '400px', overflowY: 'auto', backgroundColor: '#1a202c', borderRadius: '4px', padding: '10px' }}>
                  {realTweets.map((tweet, index) => (
                    <div key={tweet.id} style={{ 
                      padding: '10px', 
                      marginBottom: '8px', 
                      backgroundColor: selectedTweetIds.has(tweet.id) ? '#374151' : '#2d3748', 
                      borderRadius: '4px',
                      borderLeft: `3px solid ${selectedTweetIds.has(tweet.id) ? '#f7931a' : '#4a5568'}`,
                      cursor: 'pointer'
                    }}
                    onClick={() => toggleTweetSelection(tweet.id)}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                        <div style={{ fontSize: '12px', color: '#a0aec0' }}>
                          {new Date(tweet.created_at).toLocaleDateString()} • 
                          ❤️ {tweet.public_metrics?.like_count || 0} • 
                          🔄 {tweet.public_metrics?.retweet_count || 0}
                        </div>
                        <input 
                          type="checkbox" 
                          checked={selectedTweetIds.has(tweet.id)}
                          onChange={() => toggleTweetSelection(tweet.id)}
                          style={{ marginLeft: '10px' }}
                        />
                      </div>
                      <div style={{ fontSize: '14px', lineHeight: '1.4' }}>
                        {tweet.text.substring(0, 200)}{tweet.text.length > 200 ? '...' : ''}
                      </div>
                      <div style={{ fontSize: '12px', marginTop: '5px' }}>
                        <a href={tweet.url} target="_blank" rel="noopener noreferrer" style={{ color: '#3182ce', textDecoration: 'none' }}>
                          🔗 View Tweet
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          
          {/* Queue Stats */}
          {queueStats && (
            <div style={{ marginBottom: '20px', padding: '20px', backgroundColor: '#374151', borderRadius: '8px' }}>
              <h3 style={{ marginBottom: '15px' }}>📈 Curation Queue Stats</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px' }}>
                <div style={{ textAlign: 'center', padding: '10px', backgroundColor: '#1a202c', borderRadius: '4px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#3182ce' }}>{queueStats.total || 0}</div>
                  <div style={{ fontSize: '12px', color: '#a0aec0' }}>TOTAL QUEUED</div>
                </div>
                <div style={{ textAlign: 'center', padding: '10px', backgroundColor: '#1a202c', borderRadius: '4px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f7931a' }}>{queueStats.pending || 0}</div>
                  <div style={{ fontSize: '12px', color: '#a0aec0' }}>PENDING FETCH</div>
                </div>
                <div style={{ textAlign: 'center', padding: '10px', backgroundColor: '#1a202c', borderRadius: '4px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#4CAF50' }}>{queueStats.completed || 0}</div>
                  <div style={{ fontSize: '12px', color: '#a0aec0' }}>COMPLETED</div>
                </div>
                <div style={{ textAlign: 'center', padding: '10px', backgroundColor: '#1a202c', borderRadius: '4px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ff6b6b' }}>{queueStats.failed || 0}</div>
                  <div style={{ fontSize: '12px', color: '#a0aec0' }}>FAILED</div>
                </div>
              </div>
            </div>
          )}
          
          {/* Curated Queue Results - Enhanced Review Interface */}
          {curatedQueue.length > 0 && (
            <div style={{ padding: '20px', backgroundColor: '#374151', borderRadius: '8px' }}>
              <h3 style={{ marginBottom: '15px' }}>🔍 Tweet Review Queue ({curatedQueue.length}) - Anti-Hallucination Check</h3>
              
              {/* Review Instructions */}
              <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#2d3748', borderRadius: '6px', borderLeft: '4px solid #f7931a' }}>
                <strong>🛡️ Manual Review Required:</strong>
                <ul style={{ marginLeft: '20px', marginTop: '8px', color: '#a0aec0', fontSize: '14px' }}>
                  <li>✅ Verify each tweet is <strong>real</strong> by clicking "View Original Tweet"</li>
                  <li>✅ Check the tweet content makes sense and contains Bitcoin predictions</li>
                  <li>✅ Approve only genuine tweets - reject anything suspicious</li>
                  <li>⚠️ Only approved tweets will be analyzed with AI to prevent hallucination</li>
                </ul>
              </div>
              
              <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                {curatedQueue.slice(0, 20).map((item, index) => (
                  <div key={index} style={{ 
                    padding: '15px', 
                    marginBottom: '10px', 
                    backgroundColor: '#1a202c', 
                    borderRadius: '4px', 
                    borderLeft: `4px solid ${
                      item.status === 'ready_for_review' ? '#f7931a' :
                      item.status === 'completed' ? '#4CAF50' : 
                      item.status === 'failed' ? '#ff6b6b' : 
                      item.status === 'approved' ? '#4CAF50' :
                      item.status === 'rejected' ? '#ff6b6b' : '#4a5568'
                    }`
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                      <div style={{ fontSize: '14px', flex: 1 }}>
                        <strong>@{item.username || item.author_username}</strong> • {item.grok_date ? new Date(item.grok_date).toLocaleDateString() : new Date(item.created_at).toLocaleDateString()}
                        <div style={{ marginTop: '4px' }}>
                          <span style={{ 
                            padding: '2px 6px', 
                            borderRadius: '12px', 
                            fontSize: '10px', 
                            backgroundColor: 
                              item.status === 'ready_for_review' ? '#f7931a20' :
                              item.status === 'completed' ? '#4CAF5020' : 
                              item.status === 'failed' ? '#ff6b6b20' : 
                              item.status === 'approved' ? '#4CAF5020' :
                              item.status === 'rejected' ? '#ff6b6b20' : '#4a556820',
                            color: 
                              item.status === 'ready_for_review' ? '#f7931a' :
                              item.status === 'completed' ? '#4CAF50' : 
                              item.status === 'failed' ? '#ff6b6b' : 
                              item.status === 'approved' ? '#4CAF50' :
                              item.status === 'rejected' ? '#ff6b6b' : '#4a5568'
                          }}>
                            {item.status === 'ready_for_review' ? 'READY FOR REVIEW' : item.status.toUpperCase()}
                          </span>
                          {item.grok_type && (
                            <span style={{ 
                              marginLeft: '5px', 
                              padding: '2px 6px', 
                              borderRadius: '12px', 
                              fontSize: '10px', 
                              backgroundColor: '#3182ce20',
                              color: '#3182ce'
                            }}>
                              {item.grok_type.replace('_', ' ').toUpperCase()}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Action Buttons for Review */}
                      {item.status === 'ready_for_review' && (
                        <div style={{ display: 'flex', gap: '8px', marginLeft: '10px' }}>
                          <button 
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#4CAF50',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              fontWeight: 'bold'
                            }}
                            onClick={() => {
                              // TODO: Add approve function
                              alert(`TODO: Approve tweet ${item.id} for AI analysis`);
                            }}
                          >
                            ✅ Approve
                          </button>
                          <button 
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#ff6b6b',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              fontWeight: 'bold'
                            }}
                            onClick={() => {
                              // TODO: Add reject function
                              alert(`TODO: Reject tweet ${item.id}`);
                            }}
                          >
                            ❌ Reject
                          </button>
                        </div>
                      )}
                    </div>
                    
                    {/* Real Tweet Content - Anti-Hallucination */}
                    {item.tweet_text && (
                      <div style={{ 
                        marginBottom: '12px', 
                        fontSize: '14px', 
                        lineHeight: '1.5',
                        padding: '12px',
                        backgroundColor: '#2d3748',
                        borderRadius: '4px',
                        border: '1px solid #4a5568'
                      }}>
                        <div style={{ marginBottom: '8px', fontSize: '12px', color: '#4CAF50', fontWeight: 'bold' }}>
                          ✅ REAL TWEET CONTENT (verified from X.com API):
                        </div>
                        "{item.tweet_text}"
                      </div>
                    )}
                    
                    {/* Engagement & Metrics */}
                    {(item.like_count || item.retweet_count || item.reply_count) && (
                      <div style={{ fontSize: '12px', color: '#a0aec0', marginBottom: '8px' }}>
                        💬 {item.like_count || 0} likes • 🔄 {item.retweet_count || 0} retweets • 💬 {item.reply_count || 0} replies
                        {item.tweet_created_at && (
                          <span style={{ marginLeft: '10px' }}>
                            📅 {new Date(item.tweet_created_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    )}
                    
                    {/* Tweet Link - Verification */}
                    {item.tweet_url && (
                      <div style={{ fontSize: '12px', marginBottom: '8px' }}>
                        <a 
                          href={item.tweet_url} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          style={{ 
                            color: '#3182ce', 
                            textDecoration: 'none',
                            fontWeight: 'bold'
                          }}
                        >
                          🔗 View Original Tweet (VERIFY IT'S REAL!)
                        </a>
                      </div>
                    )}
                    
                    {/* Show Grok analysis if completed */}
                    {item.why_aged && (
                      <div style={{ fontSize: '14px', marginBottom: '8px', padding: '10px', backgroundColor: '#374151', borderRadius: '4px', lineHeight: '1.4' }}>
                        <strong>🔍 Grok Analysis:</strong> {item.why_aged}
                      </div>
                    )}
                    
                    {/* Show error if any */}
                    {item.error_message && (
                      <div style={{ fontSize: '12px', color: '#ff6b6b', marginBottom: '8px', fontStyle: 'italic' }}>
                        ❌ Error: {item.error_message}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#374151', borderRadius: '8px', fontSize: '14px' }}>
            <strong>AI Curation Instructions:</strong>
            <ul style={{ marginLeft: '20px', marginTop: '10px', color: '#a0aec0' }}>
              <li>Select an influencer to analyze their tweets with Grok AI</li>
              <li>Grok will identify the most valuable Bitcoin prediction tweets</li>
              <li>Start with 20-50 tweets to test the AI curation quality</li>
              <li>Use up to 120 months to access their entire tweet history</li>
              <li>Use "Fetch from X API" to get full tweet data after curation</li>
              <li>AI provides confidence scores, summaries, and extracted predictions</li>
            </ul>
          </div>
        </div>
      ) : activeTab === 'link-submissions' ? (
        <LinkSubmissionsAdmin theme={theme} />
      ) : (
        <>
          {loading ? (
            <p>Loading submissions...</p>
          ) : error ? (
            <p>{error}</p>
          ) : submissions.length === 0 ? (
            <p>No {activeTab} submissions found.</p>
          ) : (
            <TableContainer>
              <Table>
                <thead>
                  <tr>
                    <Th>Expert</Th>
                <Th>Twitter</Th>
                <Th>Prediction</Th>
                <Th>Type</Th>
                <Th>Target Date</Th>
                <Th>Submitted</Th>
                <Th>Status</Th>
                <Th>Source</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
          <tbody>
            {submissions.map(submission => (
              <tr key={submission.id}>
                <Td>
                  {submission.expert_name}
                  {submission.existing_influencer_id && (
                    <div style={{ fontSize: '12px', color: '#4caf50' }}>
                      Matches: {submission.existing_influencer_name}
                    </div>
                  )}
                </Td>
                <Td>{submission.twitter_handle || 'N/A'}</Td>
                <Td>{formatPrediction(submission)}</Td>
                <Td>{submission.prediction_type || 'N/A'}</Td>
                <Td>{formatDate(submission.target_time)}</Td>
                <Td>{formatDate(submission.created_at || submission.submission_date)}</Td>
                <Td>
                  <Status status={submission.status}>
                    {submission.status.charAt(0).toUpperCase() + submission.status.slice(1)}
                  </Status>
                </Td>
                <Td>
                  {submission.source_url && (
                    <a href={submission.source_url} target="_blank" rel="noopener noreferrer">
                      Link
                    </a>
                  )}
                </Td>
                <Td>
                  <PredictionActionButton 
                    className="view"
                    onClick={() => openModal(submission, 'view')}
                  >
                    View
                  </PredictionActionButton>
                  
                  {submission.status === 'pending' && (
                    <>
                      <PredictionActionButton 
                        className="approve"
                        onClick={() => openModal(submission, 'approve')}
                      >
                        Approve
                      </PredictionActionButton>
                      <PredictionActionButton 
                        className="reject"
                        onClick={() => openModal(submission, 'reject')}
                      >
                        Reject
                      </PredictionActionButton>
                    </>
                  )}
                  
                  {(submission.status === 'approved' || submission.status === 'processed') && (
                    <PredictionActionButton 
                      className="reject"
                      onClick={() => handleDeletePrediction(submission)}
                      title="Delete entire prediction"
                    >
                      🗑️ Delete
                    </PredictionActionButton>
                  )}
                  
                  {(submission.status === 'approved' || submission.status === 'processed') && (
                    <PredictionActionButton 
                      className="reject"
                      onClick={() => handleDeletePrediction(submission)}
                      title="Delete entire prediction"
                    >
                      🗑️ Delete
                    </PredictionActionButton>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
            </TableContainer>
          )}
        </>
      )}
      
      {selectedSubmission && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>
                {modalMode === 'view' ? 'View Submission' : 
                 modalMode === 'approve' ? 'Approve Submission' : 'Reject Submission'}
              </ModalTitle>
              <CloseButton onClick={closeModal}>&times;</CloseButton>
            </ModalHeader>
            
            <div>
              {/* Edit Mode Toggle */}
              <EditToggleButton onClick={toggleEditMode}>
                {isEditMode ? '👁️ View Mode' : '✏️ Edit Mode'}
              </EditToggleButton>
              
              {/* Expert Name */}
              <FormGroup>
                <Label>Expert Name:</Label>
                {isEditMode ? (
                  <Input 
                    value={editedData.expert_name || selectedSubmission.expert_name || ''}
                    onChange={(e) => handleFieldChange('expert_name', e.target.value)}
                    placeholder="Expert name"
                  />
                ) : (
                  <p>{selectedSubmission.expert_name}</p>
                )}
              </FormGroup>
              
              {/* Twitter Handle */}
              <FormGroup>
                <Label>Twitter Handle:</Label>
                {isEditMode ? (
                  <Input 
                    value={editedData.twitter_handle || selectedSubmission.twitter_handle || ''}
                    onChange={(e) => handleFieldChange('twitter_handle', e.target.value)}
                    placeholder="@username"
                  />
                ) : (
                  <p>{selectedSubmission.twitter_handle || 'N/A'}</p>
                )}
              </FormGroup>
              
              {/* Prediction Type */}
              <FormGroup>
                <Label>Prediction Type:</Label>
                {isEditMode ? (
                  <Select 
                    value={editedData.prediction_type || selectedSubmission.prediction_type || 'target'}
                    onChange={(e) => handleFieldChange('prediction_type', e.target.value)}
                  >
                    <option value="target">Target</option>
                    <option value="range">Range</option>
                    <option value="pricetimerange">Price Time Range</option>
                    <option value="top">Top</option>
                    <option value="bottom">Bottom</option>
                    <option value="bet">Bet</option>
                    <option value="sentiment">Sentiment</option>
                  </Select>
                ) : (
                  <p>{selectedSubmission.prediction_type || 'target'}</p>
                )}
              </FormGroup>
              
              {/* Price Fields - Dynamic based on prediction type */}
              <FormGroup>
                <Label>Price Prediction (Text):</Label>
                {isEditMode ? (
                  <Input 
                    value={editedData.price_prediction || selectedSubmission.price_prediction || ''}
                    onChange={(e) => handleFieldChange('price_prediction', e.target.value)}
                    placeholder="e.g. $100,000 or Above $50,000"
                  />
                ) : (
                  <p>{selectedSubmission.price_prediction || 'N/A'}</p>
                )}
              </FormGroup>
              
              {/* Price Target */}
              <FormGroup>
                <Label>Price Target (Numeric):</Label>
                {isEditMode ? (
                  <Input 
                    type="number"
                    value={formatPriceDisplay(editedData.price_target || selectedSubmission.price_target)}
                    onChange={(e) => handleFieldChange('price_target', parsePrice(e.target.value))}
                    placeholder="100000"
                  />
                ) : (
                  <p>{selectedSubmission.price_target ? `$${Number(selectedSubmission.price_target).toLocaleString()}` : 'N/A'}</p>
                )}
              </FormGroup>
              
              {/* Price Min */}
              <FormGroup>
                <Label>Price Min:</Label>
                {isEditMode ? (
                  <Input 
                    type="number"
                    value={formatPriceDisplay(editedData.price_min || selectedSubmission.price_min)}
                    onChange={(e) => handleFieldChange('price_min', parsePrice(e.target.value))}
                    placeholder="50000"
                  />
                ) : (
                  <p>{selectedSubmission.price_min ? `$${Number(selectedSubmission.price_min).toLocaleString()}` : 'N/A'}</p>
                )}
              </FormGroup>
              
              {/* Price Max */}
              <FormGroup>
                <Label>Price Max:</Label>
                {isEditMode ? (
                  <Input 
                    type="number"
                    value={formatPriceDisplay(editedData.price_max || selectedSubmission.price_max)}
                    onChange={(e) => handleFieldChange('price_max', parsePrice(e.target.value))}
                    placeholder="150000"
                  />
                ) : (
                  <p>{selectedSubmission.price_max ? `$${Number(selectedSubmission.price_max).toLocaleString()}` : 'N/A'}</p>
                )}
              </FormGroup>
              
              {/* Target Date */}
              <FormGroup>
                <Label>Target Date:</Label>
                {isEditMode ? (
                  <Input 
                    type="date"
                    value={editedData.target_time ? new Date(editedData.target_time).toISOString().split('T')[0] : 
                           selectedSubmission.target_time ? new Date(selectedSubmission.target_time).toISOString().split('T')[0] : ''}
                    onChange={(e) => handleFieldChange('target_time', e.target.value)}
                  />
                ) : (
                  <p>{new Date(selectedSubmission.target_time).toLocaleDateString('en-GB')}</p>
                )}
              </FormGroup>
              
              {/* Prediction Made Date */}
              <FormGroup>
                <Label>Prediction Made Date:</Label>
                {isEditMode ? (
                  <Input
                    type="date"
                    value={editedData.prediction_made ? new Date(editedData.prediction_made).toISOString().split('T')[0] : 
                           selectedSubmission.prediction_made ? new Date(selectedSubmission.prediction_made).toISOString().split('T')[0] : ''}
                    onChange={(e) => handleFieldChange('prediction_made', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                ) : (
                  <p>{selectedSubmission.prediction_made ? new Date(selectedSubmission.prediction_made).toLocaleDateString('en-GB') : 'N/A'}</p>
                )}
                <p style={{ fontSize: '12px', color: '#666', margin: '4px 0 0 0' }}>
                  Enter the actual date when this prediction was made
                </p>
              </FormGroup>
              
              {/* Source URL */}
              <FormGroup>
                <Label>Source URL:</Label>
                {isEditMode ? (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Input 
                      value={editedData.source_url || selectedSubmission.source_url || ''}
                      onChange={(e) => handleFieldChange('source_url', e.target.value)}
                      placeholder="https://twitter.com/..."
                      style={{ flex: 1 }}
                    />
                    <button
                      onClick={fetchTweetFromUrl}
                      disabled={isFetchingTweet || !(editedData.source_url || selectedSubmission.source_url)}
                      style={{
                        background: isFetchingTweet ? '#555' : '#3182ce',
                        color: 'white',
                        border: 'none',
                        padding: '8px 12px',
                        borderRadius: '4px',
                        cursor: isFetchingTweet ? 'not-allowed' : 'pointer',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {isFetchingTweet ? '⏳ Fetching...' : '📥 Fetch Tweet'}
                    </button>
                  </div>
                ) : selectedSubmission.source_url ? (
                  <p>
                    <a 
                      href={selectedSubmission.source_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      {selectedSubmission.source_url}
                    </a>
                  </p>
                ) : (
                  <p>N/A</p>
                )}
              </FormGroup>
              
              {/* Quote (stored as notes) - This is the actual tweet text */}
              <FormGroup>
                <Label>Tweet Text / Quote:</Label>
                {isEditMode ? (
                  <>
                    <TextArea 
                      value={editedData.notes || selectedSubmission.notes || ''}
                      onChange={(e) => handleFieldChange('notes', e.target.value)}
                      placeholder="Paste the actual tweet text here, or click 'Fetch Tweet' above to auto-fill..."
                      rows={4}
                    />
                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                      <button
                        onClick={analyzeWithAI}
                        disabled={isRegeneratingAI || !(editedData.notes || selectedSubmission.notes)}
                        style={{
                          background: isRegeneratingAI ? '#555' : '#F7931A',
                          color: 'white',
                          border: 'none',
                          padding: '10px 16px',
                          borderRadius: '4px',
                          cursor: isRegeneratingAI ? 'not-allowed' : 'pointer',
                          fontSize: '13px',
                          fontWeight: 'bold',
                          flex: 1
                        }}
                      >
                        {isRegeneratingAI ? '⏳ Analyzing...' : '🤖 Analyze with AI (Extract Prediction Data)'}
                      </button>
                    </div>
                    <p style={{ fontSize: '11px', color: '#888', margin: '6px 0 0 0' }}>
                      💡 Paste the tweet text, then click "Analyze with AI" to auto-fill price, dates, and prediction type
                    </p>
                  </>
                ) : (
                  <p style={{ 
                    background: '#1a1a1a', 
                    border: '1px solid #333', 
                    borderRadius: '6px', 
                    padding: '15px', 
                    whiteSpace: 'pre-wrap',
                    fontStyle: 'italic'
                  }}>
                    {selectedSubmission.notes || 'N/A'}
                  </p>
                )}
                <p style={{ fontSize: '12px', color: '#666', margin: '4px 0 0 0' }}>
                  The exact prediction quote for tweet generation and additional context
                </p>
              </FormGroup>
              
              {/* Submission Status Field */}
              <FormGroup>
                <Label>Submission Status:</Label>
                {isEditMode ? (
                  <Select 
                    value={editedData.status || selectedSubmission.status || 'pending'}
                    onChange={(e) => handleFieldChange('status', e.target.value)}
                  >
                    <option value="pending">Pending - Awaiting Admin Review</option>
                    <option value="approved">Approved - Ready for Publication</option>
                    <option value="rejected">Rejected - Not Suitable</option>
                  </Select>
                ) : (
                  <p style={{ 
                    color: selectedSubmission.status === 'approved' ? 'green' : 
                           selectedSubmission.status === 'rejected' ? 'red' : '#666' 
                  }}>
                    {selectedSubmission.status?.charAt(0).toUpperCase() + selectedSubmission.status?.slice(1) || 'Pending'}
                  </p>
                )}
                <p style={{ fontSize: '12px', color: '#666', margin: '4px 0 0 0' }}>
                  Administrative status for this submission
                </p>
              </FormGroup>

              {/* Prediction Outcome Field */}
              <FormGroup>
                <Label>Prediction Outcome:</Label>
                {isEditMode ? (
                  <Select 
                    value={editedData.prediction_outcome || selectedSubmission.prediction_outcome || 'pending'}
                    onChange={(e) => handleFieldChange('prediction_outcome', e.target.value)}
                  >
                    <option value="pending">Pending - Awaiting Target Date</option>
                    <option value="success">Success - Prediction Hit Target</option>
                    <option value="miss">Miss - Prediction Failed</option>
                    <option value="partial">Partial - Close but Not Exact</option>
                    <option value="ongoing">Ongoing - Still in Progress</option>
                  </Select>
                ) : (
                  <p style={{ 
                    color: selectedSubmission.prediction_outcome === 'success' ? 'green' : 
                           selectedSubmission.prediction_outcome === 'miss' ? 'red' : 
                           selectedSubmission.prediction_outcome === 'partial' ? 'orange' : '#666' 
                  }}>
                    {selectedSubmission.prediction_outcome ? 
                      selectedSubmission.prediction_outcome.charAt(0).toUpperCase() + selectedSubmission.prediction_outcome.slice(1) : 
                      'Pending'}
                  </p>
                )}
                <p style={{ fontSize: '12px', color: '#666', margin: '4px 0 0 0' }}>
                  Whether the prediction was accurate when evaluated
                </p>
              </FormGroup>

              {/* AI Evaluation Section */}
              {selectedSubmission.ai_metadata && (() => {
                try {
                  const aiData = typeof selectedSubmission.ai_metadata === 'string' 
                    ? JSON.parse(selectedSubmission.ai_metadata) 
                    : selectedSubmission.ai_metadata;
                  
                  if (aiData.success_status || aiData.verification_notes || aiData.price_at_prediction) {
                    return (
                      <div style={{ 
                        background: 'rgba(33, 150, 243, 0.1)', 
                        border: '1px solid rgba(33, 150, 243, 0.3)',
                        borderRadius: '6px',
                        padding: '15px',
                        marginBottom: '15px'
                      }}>
                        <h3 style={{ marginTop: 0, marginBottom: '10px', color: '#2196f3' }}>
                          🤖 AI Evaluation (Grok Analysis)
                        </h3>
                        
                        {aiData.success_status && (
                          <FormGroup>
                            <Label>Evaluation Status:</Label>
                            <p style={{ 
                              color: aiData.success_status === 'success' ? '#4caf50' : 
                                     aiData.success_status === 'miss' ? '#f44336' : 
                                     aiData.success_status === 'partial' ? '#ff9800' : '#666',
                              fontWeight: 'bold',
                              textTransform: 'uppercase'
                            }}>
                              {aiData.success_status}
                            </p>
                          </FormGroup>
                        )}
                        
                        {aiData.verification_notes && (
                          <FormGroup>
                            <Label>Evaluation Reasoning:</Label>
                            <p style={{ 
                              background: '#fff', 
                              padding: '12px', 
                              borderRadius: '4px',
                              border: '1px solid #ddd',
                              whiteSpace: 'pre-wrap',
                              lineHeight: '1.6'
                            }}>
                              {aiData.verification_notes}
                            </p>
                          </FormGroup>
                        )}
                        
                        {(aiData.price_at_prediction || aiData.price_at_target) && (
                          <FormGroup>
                            <Label>Price Context:</Label>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                              {aiData.price_at_prediction && (
                                <div style={{ background: '#fff', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}>
                                  <div style={{ fontSize: '12px', color: '#666' }}>At Prediction</div>
                                  <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
                                    ${Number(aiData.price_at_prediction).toLocaleString()}
                                  </div>
                                </div>
                              )}
                              {aiData.price_at_target && (
                                <div style={{ background: '#fff', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}>
                                  <div style={{ fontSize: '12px', color: '#666' }}>At Target</div>
                                  <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
                                    ${Number(aiData.price_at_target).toLocaleString()}
                                  </div>
                                </div>
                              )}
                            </div>
                          </FormGroup>
                        )}
                        
                        {aiData.accuracy_percentage && (
                          <FormGroup>
                            <Label>Accuracy Score:</Label>
                            <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#2196f3' }}>
                              {aiData.accuracy_percentage}%
                            </p>
                          </FormGroup>
                        )}
                      </div>
                    );
                  }
                } catch (e) {
                  console.error('Error parsing ai_metadata:', e);
                }
                return null;
              })()}

              {/* Submitter Email */}
              <FormGroup>
                <Label>Submitter Email:</Label>
                {isEditMode ? (
                  <Input 
                    type="email"
                    value={editedData.submitter_email || selectedSubmission.submitter_email || ''}
                    onChange={(e) => handleFieldChange('submitter_email', e.target.value)}
                    placeholder="user@example.com"
                  />
                ) : (
                  <p>{selectedSubmission.submitter_email || 'N/A'}</p>
                )}
              </FormGroup>
              
              {selectedSubmission.existing_influencer_id && (
                <InfoBox>
                  This expert name matches an existing influencer: 
                  <strong> {selectedSubmission.existing_influencer_name}</strong>
                </InfoBox>
              )}
              
              <FormGroup>
                <Label>Admin Comment:</Label>
                <TextArea 
                  value={comment} 
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Add your comments here..."
                />
              </FormGroup>
              
              {modalMode === 'approve' && (
                <>
                  <FormGroup>
                    <Label>Select Influencer:</Label>
                    <Select
                      value={selectedInfluencerId || ''}
                      onChange={(e) => setSelectedInfluencerId(Number(e.target.value) || null)}
                    >
                      <option value="">Create New Influencer</option>
                      {influencers.map(influencer => (
                        <option 
                          key={influencer.id} 
                          value={influencer.id}
                        >
                          {influencer.name}
                        </option>
                      ))}
                    </Select>
                    <p style={{ fontSize: '12px', marginTop: '5px' }}>
                      {selectedInfluencerId ? 
                        'This prediction will be associated with the selected influencer.' : 
                        'A new influencer will be created with this name.'}
                    </p>
                  </FormGroup>
                  
                  {/* Add new fields for start and end date */}
                  <FormGroup>
                    <Label>Start Date (Optional):</Label>
                    <Input 
                      type="date" 
                      value={startDate} 
                      onChange={(e) => setStartDate(e.target.value)}
                      placeholder="YYYY-MM-DD"
                    />
                    <p style={{ fontSize: '12px', marginTop: '5px' }}>
                      When does this prediction period begin? Defaults to today.
                    </p>
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>End Date (Optional):</Label>
                    <Input 
                      type="date" 
                      value={endDate} 
                      onChange={(e) => setEndDate(e.target.value)}
                      placeholder="YYYY-MM-DD"
                    />
                    <p style={{ fontSize: '12px', marginTop: '5px' }}>
                      When does this prediction period end? Defaults to the target date.
                    </p>
                  </FormGroup>
                </>
              )}
              
              {/* Social Media Posting Section */}
              {selectedSubmission && (
                <SocialMediaSection>
                  <SocialMediaHeader>
                    <SocialMediaTitle>
                      🚀 Post to Social Media
                    </SocialMediaTitle>
                    <ToggleButton 
                      onClick={() => setShowSocialMediaSection(!showSocialMediaSection)}
                    >
                      {showSocialMediaSection ? '▲ Hide' : '▼ Show'}
                    </ToggleButton>
                  </SocialMediaHeader>
                  
                  {showSocialMediaSection && (
                    <>
                      {/* Tweet Proposals */}
                      <TweetProposalSection>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                          <h4 style={{ margin: '0', color: 'white' }}>🤖 AI-Generated Tweet Proposals</h4>
                          <button
                            onClick={regenerateAIProposals}
                            disabled={isRegeneratingAI}
                            style={{
                              background: isRegeneratingAI ? '#555' : '#F7931A',
                              color: 'white',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '4px',
                              cursor: isRegeneratingAI ? 'not-allowed' : 'pointer',
                              fontSize: '12px',
                              fontWeight: 'bold'
                            }}
                          >
                            {isRegeneratingAI ? '⏳ Generating...' : '🔄 Regenerate AI'}
                          </button>
                        </div>
                        
                        {getTweetProposals().length === 0 && !isRegeneratingAI && (
                          <div style={{ 
                            padding: '20px', 
                            backgroundColor: 'rgba(255,255,255,0.1)', 
                            borderRadius: '8px', 
                            textAlign: 'center',
                            marginBottom: '15px'
                          }}>
                            <p style={{ color: 'rgba(255,255,255,0.8)', margin: '0 0 10px 0' }}>
                              ⚠️ No AI proposals available
                            </p>
                            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', margin: 0 }}>
                              Click "Regenerate AI" to ask Grok to create tweet proposals for this submission.
                            </p>
                          </div>
                        )}
                        
                        <ProposalGrid>
                          {getTweetProposals().map((proposal: any, index: number) => (
                            <ProposalCard 
                              key={index}
                              selected={selectedTweetProposal === proposal.text}
                              onClick={() => selectTweetProposal(proposal)}
                            >
                              <ProposalStyle>{proposal.style}</ProposalStyle>
                              <ProposalText>{proposal.text}</ProposalText>
                            </ProposalCard>
                          ))}
                        </ProposalGrid>
                      </TweetProposalSection>
                      
                      {/* Custom Tweet Text */}
                      <CustomTweetSection>
                        <h4 style={{ margin: '0 0 10px 0', color: 'white' }}>✏️ Edit Tweet Text</h4>
                        <CustomTweetTextArea
                          value={customTweetText}
                          onChange={(e) => setCustomTweetText(e.target.value)}
                          placeholder="Edit your tweet text here... (Long tweets supported!)"
                          maxLength={10000}
                        />
                        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', textAlign: 'right' }}>
                          {customTweetText.length}/10,000 characters {customTweetText.length > 280 && '(Long tweet)'}
                        </div>
                      </CustomTweetSection>
                      
                      {/* Image Upload */}
                      <ImageUploadSection>
                        <h4 style={{ margin: '0 0 10px 0', color: 'white' }}>📸 Add Images (16:9 Auto-Crop)</h4>
                        
                        {/* Enhanced Paste/Drop Area */}
                        <PasteDropArea 
                          ref={socialPasteAreaRef}
                          isDragOver={isDragOverSocial}
                          isPasting={isPastingSocial}
                          onClick={() => fileInputRef.current?.click()}
                          onDragOver={handleSocialDragOver}
                          onDragLeave={handleSocialDragLeave}
                          onDrop={handleSocialDrop}
                        >
                          <PasteInstructions>
                            📋 <strong>Paste images directly:</strong> <span className="shortcut">Ctrl+V</span> / <span className="shortcut">Cmd+V</span><br/>
                            📁 <strong>Drag & drop</strong> image files here<br/>
                            📤 <strong>Click to browse</strong> files (max 4 images)
                          </PasteInstructions>
                          
                          <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
                            Screenshots from clipboard supported • No need to save files!
                          </div>
                          
                          <PasteIndicator show={isPastingSocial}>
                            ✅ Image pasted from clipboard!
                          </PasteIndicator>
                        </PasteDropArea>
                        
                        <div style={{ marginBottom: '10px' }}>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleImageSelect}
                            style={{ display: 'none' }}
                          />
                          {selectedImages.length > 0 && (
                            <ClearImagesButton onClick={clearImages}>
                              🗑️ Clear All ({selectedImages.length})
                            </ClearImagesButton>
                          )}
                        </div>
                        
                        {selectedImages.length > 0 && (
                          <ImagePreviewGrid>
                            <ImagePreviewColumn>
                              <h5>Original Images</h5>
                              <ImageGrid>
                                {imagePreviews.map((preview, index) => (
                                  <ImagePreviewContainer key={index}>
                                    <PreviewImage src={preview} alt={`Original ${index + 1}`} />
                                    <RemoveImageButton onClick={() => removeImage(index)}>
                                      ❌
                                    </RemoveImageButton>
                                  </ImagePreviewContainer>
                                ))}
                              </ImageGrid>
                            </ImagePreviewColumn>
                          </ImagePreviewGrid>
                        )}
                      </ImageUploadSection>
                      
                      {/* Tweet Pipeline Option */}
                      <div style={{ 
                        background: 'rgba(30, 58, 138, 0.2)',
                        border: '1px solid rgba(59, 130, 246, 0.3)',
                        borderRadius: '8px',
                        padding: '15px',
                        margin: '15px 0'
                      }}>
                        <label style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px',
                          color: 'white',
                          cursor: 'pointer'
                        }}>
                          <input
                            type="checkbox"
                            checked={saveToTweetPipeline}
                            onChange={(e) => setSaveToTweetPipeline(e.target.checked)}
                            style={{ 
                              width: '16px', 
                              height: '16px', 
                              accentColor: '#3b82f6' 
                            }}
                          />
                          <span style={{ fontWeight: '500' }}>
                            🚀 Save to Tweet Pipeline (for later posting with screenshots)
                          </span>
                        </label>
                        <p style={{ 
                          fontSize: '12px', 
                          color: 'rgba(255,255,255,0.7)', 
                          margin: '8px 0 0 24px' 
                        }}>
                          When checked, this tweet will be saved to the pipeline for later enhancement and posting
                        </p>
                      </div>

                      {/* Posting Buttons */}
                      <PostingButtonsRow>
                        <SocialPostButton 
                          platform="x"
                          onClick={() => postToSocialMedia('x')}
                          disabled={isPostingToX || !customTweetText.trim()}
                        >
                          {isPostingToX ? '⏳ Posting...' : '🐦 Post to X.com'}
                        </SocialPostButton>
                        <SocialPostButton 
                          platform="nostr"
                          onClick={() => postToSocialMedia('nostr')}
                          disabled={isPostingToNostr || !customTweetText.trim()}
                        >
                          {isPostingToNostr ? '⏳ Posting...' : '⚡ Post to Nostr'}
                        </SocialPostButton>
                        <SocialPostButton 
                          platform="both"
                          onClick={() => postToSocialMedia('both')}
                          disabled={isPostingToX || isPostingToNostr || !customTweetText.trim()}
                        >
                          {(isPostingToX || isPostingToNostr) ? '⏳ Posting...' : '🚀 Post to Both'}
                        </SocialPostButton>
                      </PostingButtonsRow>
                      
                      {/* Results */}
                      {socialMediaResult && (
                        <div>
                          {socialMediaResult.x && (
                            <SocialResultSection success={socialMediaResult.x.success}>
                              <strong>🐦 X.com: </strong>
                              {socialMediaResult.x.success ? 
                                `✅ Posted successfully!` : 
                                `❌ Error: ${socialMediaResult.x.error}`}
                            </SocialResultSection>
                          )}
                          {socialMediaResult.nostr && (
                            <SocialResultSection success={socialMediaResult.nostr.success}>
                              <strong>⚡ Nostr: </strong>
                              {socialMediaResult.nostr.success ? 
                                `✅ Posted to ${socialMediaResult.nostr.publishedToRelays}/${socialMediaResult.nostr.totalRelays} relays` : 
                                `❌ Error: ${socialMediaResult.nostr.error}`}
                            </SocialResultSection>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </SocialMediaSection>
              )}
              
              <ButtonGroup>
                {isEditMode && (
                  <PredictionActionButton 
                    className="approve" 
                    onClick={saveEditedData}
                  >
                    💾 Save Changes
                  </PredictionActionButton>
                )}
                
                <PredictionActionButton 
                  className="approve" 
                  onClick={handleSavePredictionDate}
                >
                  📅 Save Prediction Date
                </PredictionActionButton>
                
                {modalMode === 'approve' && (
                  <PredictionActionButton 
                    className="approve" 
                    onClick={handleApprove}
                    disabled={isSavingToPipeline}
                  >
                    {isSavingToPipeline ? '⏳ Saving...' : (
                      saveToTweetPipeline && customTweetText.trim() 
                        ? '✅ Approve & Save to Pipeline' 
                        : '✅ Approve & Import'
                    )}
                  </PredictionActionButton>
                )}
                
                {modalMode === 'reject' && (
                  <PredictionActionButton className="reject" onClick={handleReject}>
                    ❌ Reject Submission
                  </PredictionActionButton>
                )}
                
                <LogoutButton onClick={closeModal}>
                  🚪 Close
                </LogoutButton>
              </ButtonGroup>
            </div>
          </ModalContent>
        </Modal>
      )}

    </Container>
  );
};

export default AdminDashboard;
